import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import {
  Check,
  ChevronDown,
  Download,
  Image as ImageIcon,
  Loader2,
  Smile,
  Ticket,
  X,
  MailOpen,
  Pencil,
  Paperclip,
} from 'lucide-react';
import {
  IconAffiliate,
  IconArrowBackUp,
  IconBellPlus,
  IconBrandWhatsapp,
  IconBuilding,
  IconChevronLeft,
  IconCircleDashed,
  IconHistory,
  IconMessage,
  IconNote,
  IconPhone,
  IconSend2,
  IconSitemap,
  IconUserMinus,
  IconUserShare,
  IconLogout,
  IconReportSearch,
  IconMail,
  IconArrowForward,
  IconCircleCheck,
  IconClockPause,
  IconRefresh,
} from '@tabler/icons-react';
import { ComposerEmojiPicker } from './ComposerEmojiPicker';
import { BoardChatConvOsChips } from './BoardChatConvOsChips';
import { BoardChatUnclaimedOsHint } from './BoardChatUnclaimedOsHint';
import { BoardChatAccessSites } from './BoardChatAccessSites';
import {
  ComposerAttachmentDrafts,
  MessageAttachmentBubble,
} from './ComposerAttachments';
import {
  ComposerVoiceRecorder,
  VoiceMicButton,
  useComposerVoiceRecorder,
} from './ComposerVoiceRecorder';
import {
  ACCEPT_MEDIA,
  MAX_FILES_PER_DRAFT,
  buildAttachmentContent,
  classificarArquivo,
  contentTypeParaExt,
  optimizePhotoFile,
  attachmentsFromMessage,
  isImageContentType,
} from '../utils/boardChatAttachmentUtils';
import {
  fetchConversation,
  fetchConversations,
  fetchConversationOs,
  parseConversationOsResponse,
  fetchConversationSessions,
  fetchMessages,
  fetchMyChatTeams,
  fetchTenantChatTeams,
  fetchTeamAgents,
  fetchPortalCustomer,
  fetchPortalCustomerProfilePhotoUrl,
  updatePortalCustomer,
  markConversationRead,
  markConversationUnread,
  assignConversationAgent,
  unassignConversationAgent,
  transferConversationTeam,
  sendChatMessage,
  sendTypingStatus,
  updateChatStatus,
  handoffConversationOs,
  patchConversationOs,
  clearConversationOsAssignee,
  claimUnassignedConversationOs,
  presignChatAttachment,
  completeChatAttachment,
  deleteChatAttachment,
  putFileToS3,
  fetchEmailReminderCount,
  fetchEmailReminderMailOptions,
  BCH_CONVERSATION_OS_UPDATED,
  notifyConversationOsUpdated,
} from '../services/boardChatService';
import { useBoardChatCable } from '../hooks/useBoardChatCable';
import { useBoardChatRealtime } from '../context/BoardChatRealtimeProvider';
import { BoardChatOsChipMenu } from './BoardChatOsChipMenu';
import { BoardChatReturnToQueueMenu } from './BoardChatReturnToQueueMenu';
import { BoardChatPreAtendimentoCloseModal } from './BoardChatPreAtendimentoCloseModal';
import { BoardChatPreAtendimentoForwardNoteModal } from './BoardChatPreAtendimentoForwardNoteModal';
import { BoardChatContactNote } from './BoardChatContactNote';
import { BoardChatAiWriteMenu } from './BoardChatAiWriteMenu';
import { BoardChatResolvedFilters } from './BoardChatResolvedFilters';
import {
  applyResolvedFilters,
  countResolvedFilters,
  EMPTY_RESOLVED_FILTERS,
} from '../utils/boardChatResolvedFilterUtils';
import { exportResolvedConversationsXlsx } from '../utils/boardChatResolvedExport';
import {
  assigneeFromConversation,
  avatarHue,
  contactIdFromConversation,
  contactNameFromConversation,
  contactNoteFromConversation,
  conversationStatusOf,
  findMessageIndexForSession,
  formatBadge,
  formatPhoneDisplay,
  formatPhoneMaskLimited,
  firstInitialFromName,
  initialsFromName,
  isConversationInteractionLocked,
  isConversationAwaitingAssume,
  filterOsListForAgent,
  conversationOsHandoffActive,
  getPendingOsHolderAgentId,
  getOsOwnedByAgent,
  conversationHasOsOwnedByOtherAgent,
  getPendingOsOwnedByOtherAgents,
  canLeaveAttendanceForPeerOs,
  getBlockingPendingOsIds,
  formatOsIdListLabel,
  buildOsAssignmentTipRows,
  isConversationReadOnlyForAgent,
  isContestedUnassignedOs,
  isOsChipMenuConsultOnly,
  listContestedUnassignedOs,
  lastMessagePreview,
  lastMessageIsFiles,
  messageTimeLabel,
  normalizeMessageType,
  splitTextAndEmojis,
  isEmojiOnlyContent,
  phoneDigitsOnly,
  relativeTime,
  sessionStatusLabel,
  sessionTimeLabel,
  teamIdFromConversation,
  teamNameFromConversation,
  translateActivityMessage,
} from '../utils/boardChatUtils';
import {
  BOARD_CHAT_LAYOUT_DEFAULT,
  BOARD_CHAT_LAYOUTS,
} from '../utils/boardChatLayout';
import { BoardChatLayoutSwitcher } from './BoardChatLayoutSwitcher';
import { BoardChatTeamTransferMenu } from './BoardChatTeamTransferMenu';
import { BoardChatCannedResponsesMenu } from './BoardChatCannedResponsesMenu';
import { BoardChatOpenOsPanel } from './BoardChatOpenOsPanel';
import { BoardChatEmailReminderPanel } from './BoardChatEmailReminderPanel';
import {
  BoardChatEmailReminderMessageCard,
  parseEmailReminderCardContent,
} from './BoardChatEmailReminderMessageCard';
import {
  BoardChatPreAtendimentoCard,
  parsePreAtendimentoCardContent,
} from './BoardChatPreAtendimentoCard';
import {
  BoardChatEquipmentChangeCard,
  parseEquipmentChangeMessage,
} from './BoardChatEquipmentChangeCard';
import { BchTipLayer, useBchTip } from './BchFloatTip';
import { useBoardChatOsSelection } from '../hooks/useBoardChatOsSelection';
import { fetchUserProfilePhotoUrl } from '../../user-management/hooks/useUserProfilePhotos';
import './BoardChatPanel.css';

const STATUS_FILTERS = [
  { id: 'active', label: 'Ativas' },
  { id: 'unattended', label: 'Novas' },
  { id: 'pending', label: 'Pendentes' },
  { id: 'open', label: 'Abertas' },
  { id: 'resolved', label: 'Resolvidas' },
];

const LIST_GROUPS = [
  { id: 'unattended', label: 'Novas' },
  { id: 'pending', label: 'Pendentes' },
  { id: 'open', label: 'Abertas' },
];

const OPEN_OS_PANEL_ANIMATION_MS = 220;
const OS_CARD_PREFIX = 'Ordem de serviço aberta';

async function fetchAllConversationPages(query) {
  const byId = new Map();
  let meta = {};

  for (let page = 1; page <= 100; page += 1) {
    const res = await fetchConversations({ ...query, page });
    const env = res.data || {};
    const payload = Array.isArray(env.payload) ? env.payload : [];
    meta = env.meta || meta;
    const before = byId.size;
    payload.forEach((conversation) => byId.set(Number(conversation.id), conversation));
    const total = Number(meta.all_count ?? meta.count);
    if (
      payload.length === 0 ||
      byId.size === before ||
      (Number.isFinite(total) && total >= 0 && byId.size >= total)
    ) {
      break;
    }
  }

  return { payload: [...byId.values()], meta };
}

function normalizeCannedShortcut(value) {
  return String(value || '')
    .trim()
    .replace(/^\/+/, '')
    .trim()
    .toLowerCase();
}

function cannedShortcutQuery(value) {
  const typed = String(value || '').trim();
  if (!typed.startsWith('/') || /\s/.test(typed)) return null;
  return normalizeCannedShortcut(typed);
}

function filterCannedByShortcut(query, responses = []) {
  if (query == null) return [];
  return responses.filter((r) => normalizeCannedShortcut(r.shortcut).startsWith(query));
}

function buildOsCardContent({ osNumber, serie, patrimonio, defect }) {
  return [
    OS_CARD_PREFIX,
    `OS: ${osNumber}`,
    serie || patrimonio ? `Equipamento: ${[serie, patrimonio].filter(Boolean).join(' · ')}` : null,
    defect ? `Defeito: ${defect}` : null,
  ].filter(Boolean).join('\n');
}

function parseOsCardContent(content) {
  const text = String(content || '');
  if (!text.startsWith(OS_CARD_PREFIX)) return null;
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  const get = (label) => {
    const line = lines.find((item) => item.toLowerCase().startsWith(`${label.toLowerCase()}:`));
    return line ? line.slice(label.length + 1).trim() : '';
  };
  const equipment = get('Equipamento');
  const [serie, patrimonio] = equipment.split('·').map((part) => part.trim());
  return {
    osNumber: get('OS'),
    serie,
    patrimonio,
    defect: get('Defeito'),
  };
}

/** Texto da bolha com emojis ampliados. */
function renderChatBubbleText(content) {
  const parts = splitTextAndEmojis(content);
  if (!parts.length) return null;
  if (parts.length === 1 && parts[0].type === 'text') return parts[0].value;
  return parts.map((part, idx) =>
    part.type === 'emoji' ? (
      <span key={`e-${idx}`} className="bch-emoji">
        {part.value}
      </span>
    ) : (
      <React.Fragment key={`t-${idx}`}>{part.value}</React.Fragment>
    )
  );
}

function bubbleClassFor(dir, content) {
  const emojiOnly = isEmojiOnlyContent(content) ? ' bch-bubble--emoji' : '';
  return `bch-bubble bch-bubble--${dir}${emojiOnly}`;
}

/** Mensagem pública do botão "Abrir ordem de serviço" — não é nota de pré-atendimento. */
function textIsStandardOsCard(text) {
  const t = String(text || '');
  if (parsePreAtendimentoCardContent(t)) return false;
  return t.startsWith(OS_CARD_PREFIX) || /(?:^|\n)OS:\s*[A-Za-z0-9_-]+/i.test(t);
}

/**
 * Vínculo de pré-atendimento: time de pré-atendimento, ou nota "Abertura de OS".
 * A citação `OS:` da abertura padrão fica de fora.
 */
function filterConversationOsForPreAtendimento(osList, { messages, teamIsPre }) {
  const items = Array.isArray(osList) ? osList : [];
  const preIds = [];
  for (const m of messages || []) {
    const pre = parsePreAtendimentoCardContent(m?.content);
    if (pre?.osNumber) preIds.push(String(pre.osNumber).trim());
  }
  return items.filter((item) => {
    const id = String(item?.osId || item?.os_id || '').trim();
    if (preIds.some((preId) => osIdsMatch(preId, id))) return true;
    if (parsePreAtendimentoCardContent(item?.message)) return true;
    if (textIsStandardOsCard(item?.message)) return false;
    return teamIsPre;
  });
}

/** Compara códigos de OS (zeros à esquerda em IDs numéricos). */
function osIdsMatch(a, b) {
  const x = String(a || '').trim();
  const y = String(b || '').trim();
  if (!x || !y) return false;
  if (x === y) return true;
  if (/^\d+$/.test(x) && /^\d+$/.test(y)) {
    return x.replace(/^0+/, '') === y.replace(/^0+/, '');
  }
  return false;
}

/** os_id citado na mensagem (content_attributes do Chatwoot / bridge). */
function messageCitedOsId(message) {
  const attrs = message?.content_attributes || message?.contentAttributes || null;
  if (!attrs || typeof attrs !== 'object') return '';
  return String(attrs.os_id ?? attrs.osId ?? '').trim();
}

/** OS associada à mensagem (attrs, card pré-atend ou card OS gerada). */
function resolveMessageOsId(message) {
  const cited = messageCitedOsId(message);
  if (cited) return cited;
  const pre = parsePreAtendimentoCardContent(message?.content);
  if (pre?.osNumber) return String(pre.osNumber).trim();
  const osCard = parseOsCardContent(message?.content);
  if (osCard?.osNumber) return String(osCard.osNumber).trim();
  return '';
}

/**
 * Filtra a thread pela OS ativa (múltiplas OS / handoff).
 * - Com os_id/card: só a OS alvo
 * - Sem os_id: herda o trecho (carry); incoming/outgoing públicos entram sempre
 *   na OS ativa do composer (cliente não cita OS — evita sumir da thread)
 * - Antes do 1º os_id: entram se o próximo trecho for a OS alvo
 */
function filterMessagesBySelectedOs(messages, selectedOsId) {
  const target = String(selectedOsId || '').trim();
  if (!target || !Array.isArray(messages)) return Array.isArray(messages) ? messages : [];
  const resolved = messages.map((m) => resolveMessageOsId(m));
  const nextOsFrom = (i) => {
    for (let j = i; j < resolved.length; j += 1) {
      if (resolved[j]) return resolved[j];
    }
    return '';
  };
  let carryOs = '';
  const out = [];
  for (let i = 0; i < messages.length; i += 1) {
    const os = resolved[i];
    if (os) {
      carryOs = os;
      if (osIdsMatch(os, target)) out.push(messages[i]);
      continue;
    }
    const kind = normalizeMessageType(messages[i]);
    // Público sem OS: visível na thread da OS selecionada (não herda OS de outro trecho)
    if (kind === 'incoming' || kind === 'outgoing') {
      out.push(messages[i]);
      continue;
    }
    if (osIdsMatch(carryOs, target)) {
      out.push(messages[i]);
      continue;
    }
    if (!carryOs && osIdsMatch(nextOsFrom(i), target)) {
      out.push(messages[i]);
    }
  }
  return out;
}

/** Monta texto mínimo de abertura a partir do vínculo MySQL (fallback do card). */
function buildSyntheticPreAtendimentoContent(osId, osItem) {
  const id = String(osId || '').trim();
  if (!id) return '';
  const raw = String(osItem?.message || '').trim();
  if (raw && parsePreAtendimentoCardContent(raw)) return raw;
  const eq = osItem?.osData?.equipamento || {};
  const equip = [eq.modelo, eq.num_serie || eq.serie, eq.patrimonio || eq.tag]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' · ');
  const defeito = String(osItem?.osData?.defeito?.descricao || '').trim();
  const lines = [`Abertura de OS ${id} para pré-atendimento`];
  if (equip) lines.push(`Equipamento: ${equip}`);
  if (defeito) lines.push(`Defeito: ${defeito}`);
  if (osItem?.osData?.acaoTomada) lines.push(`Ação tomada: ${osItem.osData.acaoTomada}`);
  if (osItem?.osData?.viuSolucao) {
    lines.push(`Usuário viu sugestão de solução: ${osItem.osData.viuSolucao}`);
  }
  if (osItem?.osData?.problema) lines.push(`Problema: ${osItem.osData.problema}`);
  const sol = osItem?.osData?.solicitante;
  if (sol?.nome) {
    lines.push(
      `Solicitante: ${sol.nome}${sol.telefone ? ` | Contato: ${sol.telefone}` : ''}`
    );
  }
  return lines.join('\n');
}

/** Anexos privados logo após o card de abertura (mesma regra do render). */
function collectOpeningAttachmentTrail(messages, startIdx) {
  const trail = [];
  let nextIdx = startIdx + 1;
  while (nextIdx < messages.length) {
    const nextMsg = messages[nextIdx];
    if (normalizeMessageType(nextMsg) !== 'private') break;
    if (parsePreAtendimentoCardContent(nextMsg?.content)) break;
    const nextAtts = attachmentsFromMessage(nextMsg);
    if (!nextAtts || nextAtts.length === 0) break;
    const cleanContent = String(nextMsg.content || '')
      .replace(/__attachment__:\{[\s\S]*?\}/g, '')
      .trim();
    if (cleanContent.length > 0) break;
    trail.push(nextMsg);
    nextIdx += 1;
  }
  return trail;
}

/**
 * Todos os cards de abertura da conversa no topo (ordem da lista de OS),
 * com fallback sintético via vínculo MySQL quando a nota Chatwoot sumiu.
 */
function prependAllPreAtendimentoOpenings(bodyMessages, allMessages, osList, { teamIsPre = false } = {}) {
  const body = Array.isArray(bodyMessages) ? bodyMessages : [];
  const source = Array.isArray(allMessages) ? allMessages : body;
  const osArr = Array.isArray(osList) ? osList : [];

  const openingsByOs = new Map();
  const consumedIds = new Set();

  for (let i = 0; i < source.length; i += 1) {
    const m = source[i];
    const pre = parsePreAtendimentoCardContent(m?.content);
    if (!pre?.osNumber) continue;
    const key = /^\d+$/.test(String(pre.osNumber))
      ? String(pre.osNumber).replace(/^0+/, '') || '0'
      : String(pre.osNumber).trim();
    if (openingsByOs.has(key)) continue;
    const trail = collectOpeningAttachmentTrail(source, i);
    openingsByOs.set(key, { main: m, trail, osNumber: String(pre.osNumber).trim() });
    if (m?.id != null) consumedIds.add(m.id);
    trail.forEach((t) => {
      if (t?.id != null) consumedIds.add(t.id);
    });
  }

  for (const item of osArr) {
    const osId = String(item?.osId || item?.os_id || '').trim();
    if (!osId) continue;
    const key = /^\d+$/.test(osId) ? osId.replace(/^0+/, '') || '0' : osId;
    if (openingsByOs.has(key)) continue;
    if (textIsStandardOsCard(item?.message)) continue;
    if (!teamIsPre && !parsePreAtendimentoCardContent(item?.message)) continue;
    const content = buildSyntheticPreAtendimentoContent(osId, item);
    if (!parsePreAtendimentoCardContent(content)) continue;
    openingsByOs.set(key, {
      main: {
        id: `synthetic-pre-${osId}`,
        content,
        private: true,
        message_type: 1,
        created_at: item?.createdAt || item?.created_at || null,
        _syntheticPreAtendimento: true,
      },
      trail: [],
      osNumber: osId,
    });
  }

  const orderedKeys = [];
  const seenKey = new Set();
  for (const item of osArr) {
    const osId = String(item?.osId || item?.os_id || '').trim();
    if (!osId) continue;
    const key = /^\d+$/.test(osId) ? osId.replace(/^0+/, '') || '0' : osId;
    if (!openingsByOs.has(key) || seenKey.has(key)) continue;
    seenKey.add(key);
    orderedKeys.push(key);
  }
  for (const key of openingsByOs.keys()) {
    if (seenKey.has(key)) continue;
    seenKey.add(key);
    orderedKeys.push(key);
  }

  const openings = [];
  for (const key of orderedKeys) {
    const block = openingsByOs.get(key);
    if (!block) continue;
    openings.push(block.main, ...block.trail);
  }

  const rest = body.filter((m) => {
    if (m?.id != null && consumedIds.has(m.id)) return false;
    if (parsePreAtendimentoCardContent(m?.content)) return false;
    return true;
  });

  return openings.length ? [...openings, ...rest] : rest;
}

function osCiteSummary(osId, osList) {
  const id = String(osId || '').trim();
  if (!id) return null;
  const item = (osList || []).find((o) => osIdsMatch(o?.osId, id));
  const eq = item?.osData?.equipamento || {};
  const serie = String(eq.num_serie || eq.serie || eq.numSerie || '').trim();
  const patrimonio = String(eq.patrimonio || eq.tag || '').trim();
  const detail = [serie, patrimonio].filter(Boolean).join(' · ');
  return { id, detail };
}

function MessageOsCitation({ osId, osList }) {
  const cite = osCiteSummary(osId, osList);
  if (!cite) return null;
  return (
    <div
      className="bch-msg-os-cite"
      title={cite.detail ? `OS #${cite.id} · ${cite.detail}` : `OS #${cite.id}`}
    >
      <Ticket size={11} strokeWidth={2} aria-hidden />
      <span className="bch-msg-os-cite-num">OS #{cite.id}</span>
      {cite.detail ? (
        <span className="bch-msg-os-cite-detail">{cite.detail}</span>
      ) : null}
    </div>
  );
}

function BoardChatOsMessageCard({ data }) {
  return (
    <div className="bch-os-msg-card">
      <span className="bch-os-msg-icon" aria-hidden="true">
        <Ticket size={17} strokeWidth={1.8} />
      </span>
      <span className="bch-os-msg-body">
        <strong>Ordem de serviço aberta</strong>
        <span>OS {data?.osNumber || 'gerada'}</span>
        {data?.serie || data?.patrimonio ? (
          <small>
            {[data.serie, data.patrimonio].filter(Boolean).join(' · ')}
          </small>
        ) : null}
      </span>
    </div>
  );
}

function CountBadge({ value, className = '' }) {
  const label = formatBadge(value);
  if (!label) return null;
  return (
    <span
      className={`column-filter-badge fechamento-nav-badge ec-av-kanban-group-pill-badge bch-count-badge${
        className ? ` ${className}` : ''
      }`}
    >
      {label}
    </span>
  );
}

function Avatar({ name, size = 30, fontSize, photoUrl, assignee, tipProps, tipExtra }) {
  const hue = avatarHue(name);
  const photo = photoUrl ? String(photoUrl).trim() : '';
  const assigneeName = assignee?.name ? String(assignee.name).trim() : '';
  const tipLabel = assigneeName ? `Sendo atendido por @${assigneeName}` : '';
  const tipPayload =
    tipLabel && Array.isArray(tipExtra?.rows) && tipExtra.rows.length
      ? { title: tipLabel, rows: tipExtra.rows }
      : tipLabel;
  const tip = tipProps && tipPayload ? tipProps(tipPayload) : null;
  const face = photo ? (
    <span
      className="bch-avatar bch-avatar--photo"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <img src={photo} alt="" />
    </span>
  ) : (
    <span
      className="bch-avatar"
      style={{
        width: size,
        height: size,
        fontSize: fontSize || (size < 24 ? 8 : 12),
        background: `hsl(${hue} 48% 46%)`,
      }}
      aria-hidden="true"
    >
      {initialsFromName(name)}
    </span>
  );

  if (!assigneeName) return face;

  return (
    <span className="bch-avatar-wrap" style={{ width: size, height: size }}>
      {face}
      <span
        className="bch-avatar-assignee"
        aria-label={tipLabel}
        {...(tip || {})}
      >
        {assignee.initial || firstInitialFromName(assigneeName)}
      </span>
    </span>
  );
}

function RatingStars({ value }) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const num = Number(value);
  return (
    <div className="bch-rating" title={`Avaliação ${num.toFixed(2).replace('.', ',')}`}>
      <div className="bch-rating-stars" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, num - i)) * 100;
          return (
            <span key={i} style={{ position: 'relative', width: 13, height: 13, display: 'inline-block' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#e2e3e8">
                <path d="M12 3l2.7 5.9 6.3.6-4.8 4.3 1.4 6.3-5.6-3.3-5.6 3.3 1.4-6.3-4.8-4.3 6.3-.6z" />
              </svg>
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: 13,
                  overflow: 'hidden',
                  width: `${fill}%`,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="#f5a623">
                  <path d="M12 3l2.7 5.9 6.3.6-4.8 4.3 1.4 6.3-5.6-3.3-5.6 3.3 1.4-6.3-4.8-4.3 6.3-.6z" />
                </svg>
              </span>
            </span>
          );
        })}
      </div>
      <span className="bch-rating-label">{num.toFixed(2).replace('.', ',')}</span>
    </div>
  );
}

function ContractsTooltip({ open, anchorRef, onClose, contratos }) {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef?.current) {
      setCoords(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({
        left: rect.left,
        top: rect.bottom + 8,
        minWidth: Math.max(rect.width, 220),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onDoc = (e) => {
      if (anchorRef?.current?.contains(e.target)) return;
      if (e.target.closest?.('.bch-contracts-tooltip')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !coords) return null;

  return createPortal(
    <div
      className="bch-contracts-tooltip"
      style={{ left: coords.left, top: coords.top, minWidth: coords.minWidth }}
      role="dialog"
      aria-label="Contratos vinculados"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <p className="bch-contracts-tooltip-title">Contratos vinculados</p>
      <div className="bch-contracts-tooltip-list">
        {(contratos || []).map((code) => (
          <div className="bch-contracts-tooltip-item" key={code}>
            {code}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

export function BoardChatPanel({
  open,
  onClose,
  session,
  onBadgesRefresh,
  layout = BOARD_CHAT_LAYOUT_DEFAULT,
  onLayoutChange,
  onOpenOrderDetails,
  onEncaminharOs,
  layoutLocked = false,
  underHeader = false,
  initialTeamId = null,
}) {
  const [teams, setTeams] = useState([]);
  const [tenantTeams, setTenantTeams] = useState([]);
  const [tenantTeamsLoading, setTenantTeamsLoading] = useState(false);
  const [transferringTeam, setTransferringTeam] = useState(false);
  const [dragConvId, setDragConvId] = useState(null);
  const [dropTeamId, setDropTeamId] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [resolvedFilters, setResolvedFilters] = useState(EMPTY_RESOLVED_FILTERS);
  const [exportingResolved, setExportingResolved] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [meta, setMeta] = useState({});
  const [listLoading, setListLoading] = useState(false);
  const [listGroupsOpen, setListGroupsOpen] = useState({
    unattended: true,
    pending: true,
    open: true,
  });
  const [activeInboxCount, setActiveInboxCount] = useState(0);
  const [inboxBuckets, setInboxBuckets] = useState({ nova: 0, pending: 0, open: 0 });
  /** Contagens Nova/Pendente/Aberta por setor (team id) */
  const [teamBuckets, setTeamBuckets] = useState({});
  /** Team id ao qual `conversations` realmente pertence (evita falso unread na troca de setor). */
  const conversationsTeamIdRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  /** Conversation id das mensagens atualmente em tela (evita flash/troca errada). */
  const [messagesForConvId, setMessagesForConvId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionIdx, setActiveSessionIdx] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  /** Indicador visual atrasado — evita flash em conexões rápidas */
  const [threadLoadVisible, setThreadLoadVisible] = useState(false);
  const [threadEnterKey, setThreadEnterKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [cannedResponses, setCannedResponses] = useState([]);
  const [openOsFlow, setOpenOsFlow] = useState(false);
  const [openOsPanelVisible, setOpenOsPanelVisible] = useState(false);
  const [openEmailFlow, setOpenEmailFlow] = useState(false);
  const [openEmailPanelVisible, setOpenEmailPanelVisible] = useState(false);
  const [emailReminderCount, setEmailReminderCount] = useState(0);
  const [emailMailOptions, setEmailMailOptions] = useState({
    available: false,
    needsEmpresaPick: false,
    empresas: [],
    fromOptions: [],
    source: null,
    portalBaseUrl: '',
  });
  const [asPrivate, setAsPrivate] = useState(false);
  /** Aviso ao tentar Resposta com OS encaminhada selecionada (fecha com X → mantém anotação). */
  const [forwardedOsReplyNoticeOsId, setForwardedOsReplyNoticeOsId] = useState(null);
  /** Prompt ao tentar Resposta com OS adiada: oferecer Retomar. */
  const [snoozedOsReplyPromptOsId, setSnoozedOsReplyPromptOsId] = useState(null);
  const [snoozedOsResumeBusy, setSnoozedOsResumeBusy] = useState(false);
  /** OS citada no composer (pré-atendimento — só quando há OS vinculada na conversa). */
  const [composerOsId, setComposerOsId] = useState(null);
  const [sending, setSending] = useState(false);
  const [returningToQueue, setReturningToQueue] = useState(false);
  const [returnQueuePicker, setReturnQueuePicker] = useState(null);
  const [statusBusy, setStatusBusy] = useState(null); // pending | open | resolved
  const [threadLeaving, setThreadLeaving] = useState(false);
  const [error, setError] = useState('');
  const [clientInfoOpen, setClientInfoOpen] = useState(true);
  const [openOrdersOpen, setOpenOrdersOpen] = useState(true);
  const [moreInfoOpen, setMoreInfoOpen] = useState(true);
  const [conversationOsListRaw, setConversationOsList] = useState([]);
  const selectedTeamIsPre = React.useMemo(() => {
    const currentTeamId = teamIdFromConversation(selectedConv) || teamId;
    const team =
      tenantTeams.find((t) => Number(t.id) === Number(currentTeamId)) ||
      teams.find((t) => Number(t.id) === Number(currentTeamId));
    return Boolean(
      team?.isPreAtendimento || /pr[eé][\s-]atendimento/i.test(team?.name || '')
    );
  }, [selectedConv, teamId, tenantTeams, teams]);
  const conversationOsList = React.useMemo(
    () =>
      filterConversationOsForPreAtendimento(conversationOsListRaw, {
        messages,
        teamIsPre: selectedTeamIsPre,
      }),
    [conversationOsListRaw, messages, selectedTeamIsPre]
  );
  const [osDeletedNotice, setOsDeletedNotice] = useState(null);
  const [conversationOsLoading, setConversationOsLoading] = useState(false);
  /** Cache de OS por conversa — chips na lista do pré-atendimento. */
  const [osByConvId, setOsByConvId] = useState({});
  const [listRefreshing, setListRefreshing] = useState(false);
  const pendingComposerOsIdRef = useRef(null);
  const osPrefetchDoneRef = useRef(new Set());
  const conversationsRef = useRef([]);
  const isListPreAtendimentoRef = useRef(false);
  const [osChipMenu, setOsChipMenu] = useState(null);
  const [closeOsItem, setCloseOsItem] = useState(null);
  const [forwardNoteItem, setForwardNoteItem] = useState(null);
  const [osActionBusy, setOsActionBusy] = useState(false);
  const [preAtendHandoffAgents, setPreAtendHandoffAgents] = useState([]);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [claimUnassignedBusy, setClaimUnassignedBusy] = useState(false);
  const [assumeAttendanceBusy, setAssumeAttendanceBusy] = useState(false);
  /** Trava do bridge após repasse: outro agente ainda tem OS pendente. */
  const [pendingOsHolder, setPendingOsHolder] = useState(null);
  const pendingOsHolderWasActiveRef = useRef(false);
  const [showPastSessions, setShowPastSessions] = useState(false);
  const [pastSessionsVisible, setPastSessionsVisible] = useState(8);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [aiWriteOpen, setAiWriteOpen] = useState(false);
  const [portalCustomer, setPortalCustomer] = useState(null);
  const [portalCustomerLoading, setPortalCustomerLoading] = useState(false);
  const [portalCustomerError, setPortalCustomerError] = useState('');
  /** Cache de foto Customers (profile_photo_url) por conversationId */
  const [contactPhotoByConv, setContactPhotoByConv] = useState({});
  const [assigneePhotoUrl, setAssigneePhotoUrl] = useState(null);
  const [contractsTooltipOpen, setContractsTooltipOpen] = useState(false);
  const [contactTyping, setContactTyping] = useState(null);
  const [editingCargo, setEditingCargo] = useState(false);
  const [cargoDraft, setCargoDraft] = useState('');
  const [cargoSaving, setCargoSaving] = useState(false);
  const [cargoEditError, setCargoEditError] = useState('');
  const [editingPhones, setEditingPhones] = useState(false);
  const [foneFixoDraft, setFoneFixoDraft] = useState('');
  const [foneWhatsDraft, setFoneWhatsDraft] = useState('');
  const [phonesSaving, setPhonesSaving] = useState(false);
  const [phonesEditError, setPhonesEditError] = useState('');
  const [attachmentDrafts, setAttachmentDrafts] = useState([]);
  const { tip, tipProps: tipPropsBase, hideTip } = useBchTip();
  const [tipsReady, setTipsReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setTipsReady(false);
      hideTip();
      return undefined;
    }
    hideTip();
    setTipsReady(false);
    const t = window.setTimeout(() => setTipsReady(true), 450);
    return () => window.clearTimeout(t);
  }, [open, hideTip]);

  const tipProps = useCallback(
    (text, place = 'below') => {
      const base = tipPropsBase(text, place);
      return {
        ...base,
        onMouseEnter: (e) => {
          if (!tipsReady) return;
          base.onMouseEnter?.(e);
        },
        onFocus: (e) => {
          if (!tipsReady) return;
          base.onFocus?.(e);
        },
      };
    },
    [tipPropsBase, tipsReady]
  );
  const { validOsNumber } = useBoardChatOsSelection({
    enabled: open && Boolean(selectedId),
    conversationId: selectedId,
  });

  const effectiveOsList = React.useMemo(() => {
    const map = new Map();
    for (const item of conversationOsList || []) {
      const id = String(item?.osId || item || '').trim();
      if (!id) continue;
      map.set(id, {
        ...item,
        osId: id,
        osData: item?.osData || null,
        message: item?.message || null,
        createdAt: item?.createdAt || null,
        status: item?.status || 'pending',
        trackable: item?.trackable !== false,
        assignedAgentId: item?.assignedAgentId ?? item?.assigned_agent_id ?? null,
      });
    }
    // Só enriquece createdAt de OS já vinculadas — não recria vínculo fantasma
    // a partir do texto das mensagens (bloqueava Resolver após DELETE no bridge).
    for (const m of messages || []) {
      const text = String(m?.content || '');
      const matchPre = text.match(/Abertura de OS\s+([A-Za-z0-9_-]+)/i);
      if (matchPre && matchPre[1]) {
        const id = matchPre[1].trim();
        if (map.has(id) && !map.get(id)?.createdAt && m?.created_at) {
          map.set(id, { ...map.get(id), createdAt: m.created_at });
        }
      }
      const matchComp = text.match(/(?:^|\n)OS:\s*([A-Za-z0-9_-]+)/i);
      if (matchComp && matchComp[1]) {
        const id = matchComp[1].trim();
        if (map.has(id) && !map.get(id)?.createdAt && m?.created_at) {
          map.set(id, { ...map.get(id), createdAt: m.created_at });
        }
      }
    }
    const all = Array.from(map.values());
    return filterOsListForAgent(all, session?.chatwootUserId);
  }, [conversationOsList, messages, session?.chatwootUserId]);

  const osHandoffActive = React.useMemo(
    () => conversationOsHandoffActive(conversationOsList),
    [conversationOsList]
  );

  /** Só OS vindas da API (validáveis no bridge) — base do seletor do composer. */
  const composerOsOptions = React.useMemo(() => {
    const scoped = filterOsListForAgent(conversationOsList, session?.chatwootUserId);
    const seen = new Set();
    const out = [];
    for (const item of scoped || []) {
      const id = String(item?.osId || item?.os_id || '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({
        osId: id,
        osData: item?.osData || null,
        status: String(item?.status || 'pending').toLowerCase(),
        assignedAgentId: item?.assignedAgentId ?? item?.assigned_agent_id ?? null,
      });
    }
    return out;
  }, [conversationOsList, session?.chatwootUserId]);

  useEffect(() => {
    if (!composerOsOptions.length) {
      setComposerOsId(null);
      return;
    }
    const pending = pendingComposerOsIdRef.current;
    if (pending && composerOsOptions.some((o) => o.osId === pending)) {
      pendingComposerOsIdRef.current = null;
      setComposerOsId(pending);
      return;
    }
    pendingComposerOsIdRef.current = null;
    setComposerOsId((prev) => {
      if (prev && composerOsOptions.some((o) => o.osId === prev)) return prev;
      return composerOsOptions[0].osId;
    });
  }, [composerOsOptions, selectedId]);

  const composerOsStatusHint = React.useMemo(() => {
    const st = String(
      composerOsOptions.find((o) => o.osId === composerOsId)?.status || ''
    ).toLowerCase();
    if (st === 'closed') return 'fechada';
    if (st === 'forwarded') return 'encaminhada';
    if (st === 'snoozed') return 'adiada';
    return null;
  }, [composerOsOptions, composerOsId]);

  const composerOsIsForwarded = composerOsStatusHint === 'encaminhada';
  const composerOsIsSnoozed = composerOsStatusHint === 'adiada';
  const composerOsReplyRestricted = composerOsIsForwarded || composerOsIsSnoozed;
  const prevComposerOsReplyRestrictedRef = useRef(false);

  // OS encaminhada/adiada → Anotação interna; ao sair para OS liberada → Resposta.
  useEffect(() => {
    if (composerOsReplyRestricted) {
      setAsPrivate(true);
      if (!composerOsIsSnoozed) setSnoozedOsReplyPromptOsId(null);
      if (!composerOsIsForwarded) setForwardedOsReplyNoticeOsId(null);
      prevComposerOsReplyRestrictedRef.current = true;
      return;
    }
    setForwardedOsReplyNoticeOsId(null);
    setSnoozedOsReplyPromptOsId(null);
    if (prevComposerOsReplyRestrictedRef.current && !pendingOsHolder) {
      setAsPrivate(false);
    }
    prevComposerOsReplyRestrictedRef.current = false;
  }, [
    composerOsId,
    composerOsReplyRestricted,
    composerOsIsForwarded,
    composerOsIsSnoozed,
    pendingOsHolder,
  ]);

  useEffect(() => {
    setForwardedOsReplyNoticeOsId(null);
    setSnoozedOsReplyPromptOsId(null);
  }, [selectedId]);

  const threadMessages = React.useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];
    // Sem OS própria após handoff: não esconde a thread — peer do time lê o histórico.
    // (Composer/ações continuam travados por interactionLocked / actionsLocked.)
    if (osHandoffActive && !composerOsOptions.length) {
      return prependAllPreAtendimentoOpenings(list, list, conversationOsList, {
        teamIsPre: selectedTeamIsPre,
      });
    }
    let scoped = list;
    if (composerOsId && (composerOsOptions.length > 1 || osHandoffActive)) {
      scoped = filterMessagesBySelectedOs(list, composerOsId);
    }
    // Todos os cards de abertura no topo; abaixo, histórico da OS ativa
    return prependAllPreAtendimentoOpenings(scoped, list, conversationOsList, {
      teamIsPre: selectedTeamIsPre,
    });
  }, [
    messages,
    composerOsOptions.length,
    composerOsId,
    osHandoffActive,
    conversationOsList,
    selectedTeamIsPre,
  ]);

  useEffect(() => {
    if (!selectedId) return;
    const cid = Number(selectedId);
    if (!Number.isFinite(cid)) return;
    setOsByConvId((prev) => ({
      ...prev,
      [cid]: Array.isArray(conversationOsList) ? conversationOsList : [],
    }));
    osPrefetchDoneRef.current.add(cid);
  }, [selectedId, conversationOsList]);

  useEffect(() => {
    if (validOsNumber) setMoreInfoOpen(true);
  }, [validOsNumber]);
  const draftRef = useRef(null);
  const imageInputRef = useRef(null);
  const contractsAnchorRef = useRef(null);
  const agentTypingTimeoutRef = useRef(null);
  const agentTypingActiveRef = useRef(false);
  const agentTypingConvRef = useRef(null);
  const contactTypingFallbackRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageElsRef = useRef([]);
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  const clearThreadSelection = useCallback(() => {
    setSelectedId(null);
    setSelectedConv(null);
    setMessages([]);
    setMessagesForConvId(null);
    setConversationOsList([]);
    setSessions([]);
    setActiveSessionIdx(null);
  }, []);
  const markReadLiveTimerRef = useRef(null);
  const suppressBadgesUntilMarkReadRef = useRef(false);
  const { setViewingConversationId } = useBoardChatRealtime() || {};

  useEffect(() => {
    if (!(composerOsId && (composerOsOptions.length > 1 || osHandoffActive))) return;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    });
  }, [composerOsId, composerOsOptions.length, selectedId, osHandoffActive]);

  const isPreAtendimento = Boolean(
    selectedTeamIsPre ||
      (messages || []).some((m) => parsePreAtendimentoCardContent(m.content)) ||
      (conversationOsList || []).some((item) =>
        parsePreAtendimentoCardContent(item?.message)
      )
  );

  const handleOpenOsFlow = useCallback(() => {
    if (conversationStatusOf(selectedConv) === 'resolved') return;
    if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    if (
      isConversationAwaitingAssume(selectedConv, session?.chatwootUserId, conversationOsList, {
        isPreAtendimento,
      })
    )
      return;
    setOpenEmailFlow(false);
    setOpenEmailPanelVisible(false);
    setOpenOsFlow(true);
    window.setTimeout(() => setOpenOsPanelVisible(true), 20);
  }, [selectedConv, session?.chatwootUserId, conversationOsList, isPreAtendimento]);

  const handleCloseOsFlow = useCallback(() => {
    setOpenOsPanelVisible(false);
    window.setTimeout(() => setOpenOsFlow(false), OPEN_OS_PANEL_ANIMATION_MS);
  }, []);

  const handleOpenEmailFlow = useCallback(() => {
    if (!emailMailOptions.available) return;
    if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    if (
      isConversationAwaitingAssume(selectedConv, session?.chatwootUserId, conversationOsList, {
        isPreAtendimento,
      })
    )
      return;
    setOpenOsFlow(false);
    setOpenOsPanelVisible(false);
    setOpenEmailFlow(true);
    window.setTimeout(() => setOpenEmailPanelVisible(true), 20);
  }, [
    emailMailOptions.available,
    selectedConv,
    session?.chatwootUserId,
    conversationOsList,
    isPreAtendimento,
  ]);

  const handleCloseEmailFlow = useCallback(() => {
    setOpenEmailPanelVisible(false);
    window.setTimeout(() => setOpenEmailFlow(false), OPEN_OS_PANEL_ANIMATION_MS);
  }, []);

  const layoutClass =
    layout === BOARD_CHAT_LAYOUTS.fullscreen
      ? 'bch-layout-fullscreen'
      : layout === BOARD_CHAT_LAYOUTS.sidebar
        ? 'bch-layout-sidebar'
        : 'bch-layout-modal';

  const loadTeamBuckets = useCallback(async (teamList) => {
    const list = Array.isArray(teamList) ? teamList : [];
    if (!list.length) {
      setTeamBuckets({});
      return;
    }
    const unreadOf = (c) => {
      const n = Number(c?.unread_count ?? c?.unreadCount);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const entries = await Promise.all(
      list.map(async (t) => {
        const id = Number(t.id);
        try {
          const [openRes, pendingRes, unattRes] = await Promise.all([
            fetchConversations({ team_id: id, status: 'open' }),
            fetchConversations({ team_id: id, status: 'pending' }),
            fetchConversations({ team_id: id, conversation_type: 'unattended' }),
          ]);
          const openPayload = Array.isArray(openRes.data?.payload) ? openRes.data.payload : [];
          const pendingPayload = Array.isArray(pendingRes.data?.payload)
            ? pendingRes.data.payload
            : [];
          const unattPayload = Array.isArray(unattRes.data?.payload) ? unattRes.data.payload : [];
          const unattendedIds = new Set(unattPayload.map((c) => Number(c.id)));
          const openOnly = openPayload.filter((c) => !unattendedIds.has(Number(c.id)));
          const byId = new Map();
          [...openPayload, ...pendingPayload, ...unattPayload].forEach((c) => {
            const cid = Number(c?.id);
            if (Number.isFinite(cid) && cid > 0) byId.set(cid, c);
          });
          let unread = 0;
          byId.forEach((c) => {
            unread += unreadOf(c);
          });
          return [
            id,
            {
              nova: unattPayload.length,
              pending: pendingPayload.length,
              open: openOnly.length,
              unread,
            },
          ];
        } catch {
          return [id, { nova: 0, pending: 0, open: 0, unread: 0 }];
        }
      })
    );
    setTeamBuckets(Object.fromEntries(entries));
  }, []);

  const loadTeams = useCallback(async () => {
    try {
      const res = await fetchMyChatTeams();
      const list = Array.isArray(res.data) ? res.data : [];
      setTeams(list);
      setTeamId((prev) => {
        if (initialTeamId != null && list.some((t) => Number(t.id) === Number(initialTeamId))) {
          return Number(initialTeamId);
        }
        if (prev && list.some((t) => Number(t.id) === Number(prev))) return prev;
        return list[0]?.id ?? null;
      });
      loadTeamBuckets(list);
    } catch (e) {
      setError(e.message || 'Falha ao carregar times');
    }
  }, [loadTeamBuckets, initialTeamId]);

  useEffect(() => {
    if (initialTeamId != null) {
      setTeamId(Number(initialTeamId));
    }
  }, [initialTeamId]);

  const loadTenantTeams = useCallback(async () => {
    setTenantTeamsLoading(true);
    try {
      const res = await fetchTenantChatTeams();
      const list = Array.isArray(res.data) ? res.data : [];
      setTenantTeams(list);
    } catch {
      setTenantTeams([]);
    } finally {
      setTenantTeamsLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!teamId) {
      setConversations([]);
      conversationsTeamIdRef.current = null;
      setActiveInboxCount(0);
      return;
    }
    if (!silent) {
      setListLoading(true);
      setError('');
    }
    try {
      const fetchActiveBuckets = () =>
        Promise.all([
          fetchConversations({ team_id: teamId, status: 'open' }),
          fetchConversations({ team_id: teamId, status: 'pending' }),
          fetchConversations({ team_id: teamId, conversation_type: 'unattended' }),
        ]);

      if (statusFilter === 'active') {
        const [openRes, pendingRes, unattRes] = await fetchActiveBuckets();
        const openEnv = openRes.data || {};
        const pendingEnv = pendingRes.data || {};
        const unattEnv = unattRes.data || {};
        const unattendedList = (Array.isArray(unattEnv.payload) ? unattEnv.payload : []).map(
          (c) => ({
            ...c,
            status: 'unattended',
          })
        );
        const unattendedIds = new Set(unattendedList.map((c) => Number(c.id)));
        const pendingList = (Array.isArray(pendingEnv.payload) ? pendingEnv.payload : []).map(
          (c) => ({
            ...c,
            // Mantém status do Chatwoot; fallback só se o payload vier sem campo.
            status: conversationStatusOf(c) || 'pending',
          })
        );
        const openList = (Array.isArray(openEnv.payload) ? openEnv.payload : [])
          .filter((c) => !unattendedIds.has(Number(c.id)))
          .map((c) => ({
            ...c,
            status: conversationStatusOf(c) || 'open',
          }));
        setConversations([...unattendedList, ...pendingList, ...openList]);
        conversationsTeamIdRef.current = Number(teamId);
        const total = unattendedList.length + pendingList.length + openList.length;
        setActiveInboxCount(total);
        setInboxBuckets({
          nova: unattendedList.length,
          pending: pendingList.length,
          open: openList.length,
        });
        const activeConvs = [...unattendedList, ...pendingList, ...openList];
        const unreadSum = activeConvs.reduce((sum, c) => {
          const n = Number(c?.unread_count);
          return sum + (Number.isFinite(n) && n > 0 ? n : 0);
        }, 0);
        setTeamBuckets((prev) => ({
          ...prev,
          [Number(teamId)]: {
            nova: unattendedList.length,
            pending: pendingList.length,
            open: openList.length,
            unread: unreadSum,
          },
        }));
        setMeta({
          ...(openEnv.meta || {}),
          all_count: total,
          open_count: openList.length,
          pending_count: pendingList.length,
          unattended_count: unattendedList.length,
        });
      } else {
        const query =
          statusFilter === 'unattended'
            ? { team_id: teamId, conversation_type: 'unattended' }
            : { team_id: teamId, status: statusFilter };
        const env =
          statusFilter === 'resolved'
            ? await fetchAllConversationPages(query)
            : (await fetchConversations(query)).data || {};
        const list = Array.isArray(env.payload) ? env.payload : [];
        setConversations(
          list.map((c) => ({
            ...c,
            // Nunca inferir "resolved"/"open" do filtro da UI — Chatwoot é a fonte.
            status:
              statusFilter === 'unattended'
                ? 'unattended'
                : conversationStatusOf(c) || c.status || '',
          }))
        );
        conversationsTeamIdRef.current = Number(teamId);
        setMeta(env.meta || {});
        // Mantém badge Customer com total Ativas (Novas+Pendentes+Abertas)
        try {
          const [openRes, pendingRes, unattRes] = await fetchActiveBuckets();
          const openPayload = Array.isArray(openRes.data?.payload) ? openRes.data.payload : [];
          const pendingPayload = Array.isArray(pendingRes.data?.payload)
            ? pendingRes.data.payload
            : [];
          const unattPayload = Array.isArray(unattRes.data?.payload) ? unattRes.data.payload : [];
          const unattendedIds = new Set(unattPayload.map((c) => Number(c.id)));
          const openOnly = openPayload.filter((c) => !unattendedIds.has(Number(c.id)));
          const buckets = {
            nova: unattPayload.length,
            pending: pendingPayload.length,
            open: openOnly.length,
            unread: [...unattPayload, ...pendingPayload, ...openOnly].reduce((sum, c) => {
              const n = Number(c?.unread_count);
              return sum + (Number.isFinite(n) && n > 0 ? n : 0);
            }, 0),
          };
          setActiveInboxCount(buckets.nova + buckets.pending + buckets.open);
          setInboxBuckets({
            nova: buckets.nova,
            pending: buckets.pending,
            open: buckets.open,
          });
          setTeamBuckets((prev) => ({
            ...prev,
            [Number(teamId)]: buckets,
          }));
        } catch {
          /* badge opcional */
        }
      }
    } catch (e) {
      if (!silent) {
        setError(e.message || 'Falha ao listar conversas');
        setConversations([]);
      }
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [teamId, statusFilter]);

  const patchConversationInList = useCallback((patch) => {
    if (!patch?.id) return;
    const mergeConv = (c) => {
      if (Number(c.id) !== Number(patch.id)) return c;
      const next = { ...c, ...patch };
      if (patch.meta != null && typeof patch.meta === 'object') {
        next.meta = { ...(c.meta || {}), ...patch.meta };
      }
      if (patch.clearAssignee) {
        delete next.assignee;
        next.assigneeVerveUserId = null;
        next.hasAssigneeProfilePhoto = false;
        if (next.meta && typeof next.meta === 'object') {
          next.meta = { ...next.meta };
          delete next.meta.assignee;
        }
      }
      return next;
    };
    setConversations((prev) => prev.map(mergeConv));
    setSelectedConv((prev) => (prev && Number(prev.id) === Number(patch.id) ? mergeConv(prev) : prev));
  }, []);

  const applyConversationOsResponse = useCallback((conversationId, osRes, { notifyUi = true } = {}) => {
    const parsed = parseConversationOsResponse(osRes);
    const cid = Number(conversationId);
    setConversationOsList(parsed.items);
    if (Number.isFinite(cid) && cid > 0) {
      setOsByConvId((prev) => ({ ...prev, [cid]: parsed.items }));
    }
    if (parsed.releasedQueue) {
      patchConversationInList({ id: cid, clearAssignee: true });
    }
    if (notifyUi && parsed.removedOs.length && !parsed.wasOnlyOs) {
      const ids = parsed.removedOs.map((r) => r.osId).filter(Boolean);
      setOsDeletedNotice({
        osIds: ids,
        text:
          ids.length === 1
            ? `A OS ${ids[0]} foi excluída no ERP e desvinculada. Conclua ou encaminhe as demais OS desta conversa.`
            : `As OS ${ids.join(', ')} foram excluídas no ERP e desvinculadas. Conclua ou encaminhe as demais OS desta conversa.`,
      });
    } else if (notifyUi && parsed.removedOs.length && parsed.wasOnlyOs) {
      setOsDeletedNotice(null);
    }
    return parsed;
  }, [patchConversationInList]);

  const loadThread = useCallback(async (conversationId, { silent = false, markRead = true } = {}) => {
    if (!conversationId) return;
    if (!silent) setThreadLoading(true);
    setConversationOsLoading(true);
    try {
      const [convRes, msgRes, sessRes, osRes] = await Promise.all([
        fetchConversation(conversationId),
        fetchMessages(conversationId),
        fetchConversationSessions(conversationId).catch(() => ({ data: [] })),
        fetchConversationOs(conversationId).catch(() => ({ data: [] })),
      ]);
      setSelectedConv(convRes.data || null);
      setMessages(Array.isArray(msgRes.data) ? msgRes.data : []);
      setMessagesForConvId(Number(conversationId));
      if (!silent) setThreadEnterKey((k) => k + 1);
      const sessList = Array.isArray(sessRes.data) ? sessRes.data : [];
      setSessions(sessList);
      setActiveSessionIdx(sessList.length ? sessList.length - 1 : null);
      applyConversationOsResponse(conversationId, osRes, { notifyUi: true });

      // Espelha status real do Chatwoot na lista (evita stale do filtro Resolvidas).
      if (convRes.data) {
        const liveStatus = conversationStatusOf(convRes.data);
        patchConversationInList({
          id: Number(conversationId),
          ...(liveStatus ? { status: liveStatus } : null),
          meta: convRes.data.meta,
          assignee: convRes.data.assignee,
          unread_count: convRes.data.unread_count,
        });
      }

      if (markRead) {
        // Conversa nova / na fila (sem assignee): não auto-assume ao abrir.
        // Assumir atendimento é explícito no botão da thread.
        // Marca como lida ao abrir (zera unread_count para o time inteiro)
        markConversationRead(conversationId)
          .then(() => {
            patchConversationInList({ id: Number(conversationId), unread_count: 0 });
            onBadgesRefresh?.();
          })
          .catch(() => {});
      }
    } catch (e) {
      if (!silent) setError(e.message || 'Falha ao abrir conversa');
    } finally {
      if (!silent) setThreadLoading(false);
      setConversationOsLoading(false);
    }
  }, [onBadgesRefresh, patchConversationInList, applyConversationOsResponse]);

  const scrollToSession = useCallback(
    (sess, index) => {
      setActiveSessionIdx(index);
      const msgIdx = findMessageIndexForSession(messages, sess);
      if (msgIdx < 0) {
        if (!sess?.endedAt) {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
        return;
      }
      messageElsRef.current[msgIdx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [messages]
  );

  const refreshAll = useCallback(() => {
    loadConversations({ silent: true });
    loadTeamBuckets(teams);
    if (selectedIdRef.current) {
      loadThread(selectedIdRef.current, { silent: true, markRead: false });
    }
    // Chips OS da lista (handoff/snooze/status) — força re-fetch mesmo se já prefetched
    if (isListPreAtendimentoRef.current) {
      const ids = (conversationsRef.current || [])
        .map((c) => Number(c?.id))
        .filter((id) => Number.isFinite(id) && id > 0);
      ids.forEach((cid) => {
        osPrefetchDoneRef.current.delete(cid);
      });
      ids.forEach((cid) => {
        osPrefetchDoneRef.current.add(cid);
        fetchConversationOs(cid)
          .then((osRes) => {
            const list = parseConversationOsResponse(osRes).items;
            setOsByConvId((prev) => ({ ...prev, [cid]: list }));
          })
          .catch(() => {
            osPrefetchDoneRef.current.delete(cid);
          });
      });
    }
    // Evita badge fantasma enquanto mark-read da thread aberta está em voo
    if (!suppressBadgesUntilMarkReadRef.current) onBadgesRefresh?.();
  }, [loadConversations, loadTeamBuckets, loadThread, onBadgesRefresh, teams]);

  const handleManualListRefresh = useCallback(() => {
    if (listRefreshing) return;
    setListRefreshing(true);
    refreshAll();
    window.setTimeout(() => setListRefreshing(false), 700);
  }, [listRefreshing, refreshAll]);

  const stopAgentTyping = useCallback(() => {
    if (agentTypingTimeoutRef.current) {
      clearTimeout(agentTypingTimeoutRef.current);
      agentTypingTimeoutRef.current = null;
    }
    const convId = agentTypingConvRef.current;
    if (!agentTypingActiveRef.current || !convId) {
      agentTypingActiveRef.current = false;
      agentTypingConvRef.current = null;
      return;
    }
    agentTypingActiveRef.current = false;
    agentTypingConvRef.current = null;
    sendTypingStatus(convId, 'off').catch(() => {});
  }, []);

  const notifyAgentTyping = useCallback(() => {
    const convId = selectedIdRef.current;
    if (!convId || asPrivate) return;
    if (!agentTypingTimeoutRef.current) {
      agentTypingActiveRef.current = true;
      agentTypingConvRef.current = convId;
      sendTypingStatus(convId, 'on').catch(() => {});
    }
    if (agentTypingTimeoutRef.current) clearTimeout(agentTypingTimeoutRef.current);
    agentTypingTimeoutRef.current = setTimeout(() => {
      agentTypingTimeoutRef.current = null;
      agentTypingActiveRef.current = false;
      agentTypingConvRef.current = null;
      sendTypingStatus(convId, 'off').catch(() => {});
    }, 3000);
  }, [asPrivate]);

  const handleCableTyping = useCallback((typing) => {
    if (!typing || typing.conversationId == null) return;
    if (Number(typing.conversationId) !== Number(selectedIdRef.current)) return;
    if (typing.isOn) {
      setContactTyping({ name: typing.name || 'Cliente' });
      if (contactTypingFallbackRef.current) clearTimeout(contactTypingFallbackRef.current);
      contactTypingFallbackRef.current = setTimeout(() => {
        setContactTyping(null);
        contactTypingFallbackRef.current = null;
      }, 5000);
      return;
    }
    setContactTyping(null);
    if (contactTypingFallbackRef.current) {
      clearTimeout(contactTypingFallbackRef.current);
      contactTypingFallbackRef.current = null;
    }
  }, []);

  const handleConversationPatch = useCallback(
    (patch) => {
      const selected =
        selectedIdRef.current != null ? Number(selectedIdRef.current) : null;
      const isViewingThread =
        Boolean(open) &&
        selected != null &&
        Number(patch?.id) === selected &&
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible';
      if (isViewingThread && patch.unread_count != null && Number(patch.unread_count) > 0) {
        // Thread aberta ao vivo: não deixa unread pendente (time/badge)
        patchConversationInList({ ...patch, unread_count: 0 });
        suppressBadgesUntilMarkReadRef.current = true;
        if (markReadLiveTimerRef.current) clearTimeout(markReadLiveTimerRef.current);
        markReadLiveTimerRef.current = setTimeout(() => {
          markReadLiveTimerRef.current = null;
          markConversationRead(selected)
            .catch(() => {})
            .finally(() => {
              suppressBadgesUntilMarkReadRef.current = false;
              onBadgesRefresh?.();
            });
        }, 50);
        return;
      }
      patchConversationInList(patch);
      if (patch.unread_count != null) onBadgesRefresh?.();
    },
    [open, onBadgesRefresh, patchConversationInList]
  );

  /** Mensagem incoming na thread aberta → marca lida (sem re-assign de agente). */
  const handleMessageCreatedLive = useCallback(
    (msg) => {
      if (!msg?.isIncoming) return;
      const convId = msg.conversationId != null ? Number(msg.conversationId) : null;
      const selected =
        selectedIdRef.current != null ? Number(selectedIdRef.current) : null;
      if (!convId || !selected || convId !== selected) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      patchConversationInList({ id: convId, unread_count: 0 });
      suppressBadgesUntilMarkReadRef.current = true;
      if (markReadLiveTimerRef.current) clearTimeout(markReadLiveTimerRef.current);
      markReadLiveTimerRef.current = setTimeout(() => {
        markReadLiveTimerRef.current = null;
        markConversationRead(convId)
          .then(() => {
            if (Number(selectedIdRef.current) !== convId) return;
            patchConversationInList({ id: convId, unread_count: 0 });
          })
          .catch(() => {})
          .finally(() => {
            suppressBadgesUntilMarkReadRef.current = false;
            onBadgesRefresh?.();
          });
      }, 50);
    },
    [onBadgesRefresh, patchConversationInList]
  );

  // Mantém bolinha do setor ativo em sync com unread — só se a lista for desse setor
  useEffect(() => {
    const tid = Number(teamId);
    if (!Number.isFinite(tid) || tid <= 0) return;
    if (Number(conversationsTeamIdRef.current) !== tid) return;
    const unread = conversations.reduce((sum, c) => {
      const n = Number(c?.unread_count);
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
    setTeamBuckets((prev) => {
      const cur = prev[tid];
      if (!cur || Number(cur.unread) === unread) return prev;
      return { ...prev, [tid]: { ...cur, unread } };
    });
  }, [conversations, teamId]);

  useBoardChatCable(
    session,
    refreshAll,
    open && Boolean(session?.pubsubToken),
    handleCableTyping,
    handleConversationPatch,
    handleMessageCreatedLive
  );

  useEffect(() => {
    if (!setViewingConversationId) return undefined;
    if (open && selectedId) setViewingConversationId(selectedId);
    else setViewingConversationId(null);
    return () => setViewingConversationId(null);
  }, [open, selectedId, setViewingConversationId]);

  useEffect(() => {
    if (asPrivate) stopAgentTyping();
  }, [asPrivate, stopAgentTyping]);

  useEffect(() => {
    return () => {
      if (agentTypingTimeoutRef.current) clearTimeout(agentTypingTimeoutRef.current);
      if (contactTypingFallbackRef.current) clearTimeout(contactTypingFallbackRef.current);
      if (markReadLiveTimerRef.current) clearTimeout(markReadLiveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    loadTeams();
    loadTenantTeams();
    return undefined;
  }, [open, loadTeams, loadTenantTeams]);

  useEffect(() => {
    if (!open) return undefined;
    loadConversations();
    return undefined;
  }, [open, loadConversations]);

  // Se a conversa sumiu da lista atual (resolveu, transferiu, 0 itens), não manter thread/detalhes.
  useEffect(() => {
    if (!open || !selectedId || listLoading) return;
    if (statusFilter === 'resolved') return;
    const stillVisible = conversations.some((c) => Number(c.id) === Number(selectedId));
    if (!stillVisible) clearThreadSelection();
  }, [open, selectedId, conversations, listLoading, statusFilter, clearThreadSelection]);

  useEffect(() => {
    if (!open || !selectedId) {
      setSelectedConv(null);
      setMessages([]);
      setMessagesForConvId(null);
      setSessions([]);
      setActiveSessionIdx(null);
      setShowPastSessions(false);
      setPastSessionsVisible(8);
      setPortalCustomer(null);
      setPortalCustomerError('');
      setContractsTooltipOpen(false);
      setContactTyping(null);
      setEditingCargo(false);
      setEditingPhones(false);
      setCargoEditError('');
      setPhonesEditError('');
      setAttachmentDrafts((prev) => {
        prev.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        return [];
      });
      stopAgentTyping();
      return undefined;
    }
    setContactTyping(null);
    stopAgentTyping();
    setThreadLeaving(false);
    setOsDeletedNotice(null);
    // Limpa status da conversa anterior — não herdar "resolved" enquanto o GET não chega.
    setSelectedConv((prev) =>
      prev && Number(prev.id) === Number(selectedId) ? prev : null
    );
    loadThread(selectedId);
    setShowPastSessions(false);
    setPastSessionsVisible(8);
    return undefined;
  }, [open, selectedId, loadThread, stopAgentTyping]);

  // Só exibe skeleton/spinner se a troca demorar um pouco (evita flash)
  useEffect(() => {
    if (!threadLoading) {
      setThreadLoadVisible(false);
      return undefined;
    }
    const t = window.setTimeout(() => setThreadLoadVisible(true), 140);
    return () => window.clearTimeout(t);
  }, [threadLoading]);

  useEffect(() => {
    if (!open || !selectedId) {
      setEmailReminderCount(0);
      setEmailMailOptions({
        available: false,
        needsEmpresaPick: false,
        empresas: [],
        fromOptions: [],
        source: null,
        portalBaseUrl: '',
      });
      setOpenEmailFlow(false);
      setOpenEmailPanelVisible(false);
      return undefined;
    }
    let cancelled = false;
    fetchEmailReminderCount(selectedId)
      .then((res) => {
        if (cancelled) return;
        setEmailReminderCount(Number(res?.data?.count) || 0);
      })
      .catch(() => {
        if (!cancelled) setEmailReminderCount(0);
      });
    fetchEmailReminderMailOptions(selectedId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || res || {};
        setEmailMailOptions({
          available: Boolean(data.available),
          needsEmpresaPick: Boolean(data.needsEmpresaPick),
          empresas: Array.isArray(data.empresas) ? data.empresas : [],
          fromOptions: Array.isArray(data.fromOptions) ? data.fromOptions : [],
          source: data.source || null,
          portalBaseUrl: data.portalBaseUrl ? String(data.portalBaseUrl) : '',
        });
      })
      .catch(() => {
        if (!cancelled) {
          setEmailMailOptions({
            available: false,
            needsEmpresaPick: false,
            empresas: [],
            fromOptions: [],
            source: null,
            portalBaseUrl: '',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedId]);

  useEffect(() => {
    if (!open || !selectedId) return undefined;
    let cancelled = false;
    setPortalCustomerLoading(true);
    setPortalCustomerError('');
    setPortalCustomer(null);
    setContractsTooltipOpen(false);
    setEditingCargo(false);
    setEditingPhones(false);
    setCargoEditError('');
    setPhonesEditError('');
    fetchPortalCustomer(selectedId)
      .then(async (res) => {
        if (cancelled) return;
        const data = res.data || null;
        setPortalCustomer(data);
        let photo = '';
        if (data?.profilePhotoUrl) {
          photo = String(data.profilePhotoUrl).trim();
        } else if (data?.hasProfilePhoto) {
          try {
            const photoRes = await fetchPortalCustomerProfilePhotoUrl(selectedId);
            photo = photoRes?.data?.viewUrl ? String(photoRes.data.viewUrl).trim() : '';
          } catch {
            photo = '';
          }
        }
        if (cancelled) return;
        setContactPhotoByConv((prev) => {
          if (!photo) {
            if (!prev[selectedId]) return prev;
            const next = { ...prev };
            delete next[selectedId];
            return next;
          }
          if (prev[selectedId] === photo) return prev;
          return { ...prev, [selectedId]: photo };
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setPortalCustomer(null);
        setPortalCustomerError(e.message || 'Dados do cliente indisponíveis');
      })
      .finally(() => {
        if (!cancelled) setPortalCustomerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedId]);

  useEffect(() => {
    const uid = selectedConv?.assigneeVerveUserId;
    const hasPhoto = selectedConv?.hasAssigneeProfilePhoto === true;
    if (!uid || !hasPhoto) {
      setAssigneePhotoUrl(null);
      return undefined;
    }
    let cancelled = false;
    fetchUserProfilePhotoUrl(uid)
      .then((url) => {
        if (!cancelled) setAssigneePhotoUrl(url || null);
      })
      .catch(() => {
        if (!cancelled) setAssigneePhotoUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedConv?.id,
    selectedConv?.assigneeVerveUserId,
    selectedConv?.hasAssigneeProfilePhoto,
  ]);

  useEffect(() => {
    if (!isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    setEditingCargo(false);
    setEditingPhones(false);
    setOpenOsFlow(false);
    setOpenOsPanelVisible(false);
    setOpenEmailFlow(false);
    setOpenEmailPanelVisible(false);
    setEmojiOpen(false);
    setAiWriteOpen(false);
  }, [selectedConv, session?.chatwootUserId, conversationOsList]);

  const startCargoEdit = useCallback(() => {
    if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    hideTip();
    setCargoDraft(portalCustomer?.cargo || '');
    setCargoEditError('');
    setEditingCargo(true);
    setEditingPhones(false);
  }, [portalCustomer?.cargo, hideTip, selectedConv, session?.chatwootUserId, conversationOsList]);

  const cancelCargoEdit = useCallback(() => {
    setEditingCargo(false);
    setCargoEditError('');
    setCargoSaving(false);
  }, []);

  const saveCargoEdit = useCallback(async () => {
    if (!selectedId || cargoSaving) return;
    if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    setCargoSaving(true);
    setCargoEditError('');
    try {
      const res = await updatePortalCustomer(selectedId, {
        cargo: String(cargoDraft || '').trim(),
      });
      setPortalCustomer(res.data || null);
      setEditingCargo(false);
    } catch (e) {
      setCargoEditError(e.message || 'Falha ao salvar cargo');
    } finally {
      setCargoSaving(false);
    }
  }, [
    selectedId,
    cargoDraft,
    cargoSaving,
    selectedConv,
    session?.chatwootUserId,
    conversationOsList,
  ]);

  const startPhonesEdit = useCallback(() => {
    if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    hideTip();
    setFoneFixoDraft(formatPhoneMaskLimited(portalCustomer?.foneFixo || '', 10));
    setFoneWhatsDraft(formatPhoneMaskLimited(portalCustomer?.foneWhatsapp || '', 11));
    setPhonesEditError('');
    setEditingPhones(true);
    setEditingCargo(false);
  }, [
    portalCustomer?.foneFixo,
    portalCustomer?.foneWhatsapp,
    hideTip,
    selectedConv,
    session?.chatwootUserId,
    conversationOsList,
  ]);

  const cancelPhonesEdit = useCallback(() => {
    setEditingPhones(false);
    setPhonesEditError('');
    setPhonesSaving(false);
  }, []);

  const savePhonesEdit = useCallback(async () => {
    if (!selectedId || phonesSaving) return;
    if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList))
      return;
    const fixo = phoneDigitsOnly(foneFixoDraft);
    const wa = phoneDigitsOnly(foneWhatsDraft);
    if (fixo && fixo.length !== 10) {
      setPhonesEditError('Fone fixo deve ter 10 dígitos');
      return;
    }
    if (wa && wa.length !== 11) {
      setPhonesEditError('Fone WhatsApp deve ter 11 dígitos');
      return;
    }
    setPhonesSaving(true);
    setPhonesEditError('');
    try {
      const res = await updatePortalCustomer(selectedId, {
        foneFixo: fixo,
        foneWhatsapp: wa,
      });
      setPortalCustomer(res.data || null);
      setEditingPhones(false);
    } catch (e) {
      setPhonesEditError(e.message || 'Falha ao salvar telefones');
    } finally {
      setPhonesSaving(false);
    }
  }, [
    selectedId,
    foneFixoDraft,
    foneWhatsDraft,
    phonesSaving,
    selectedConv,
    session?.chatwootUserId,
    conversationOsList,
  ]);

  /** Atualiza custom_attributes.nota em todas as conversas do mesmo contato. */
  const applyContactNoteLocal = useCallback((contactId, note) => {
    const patchSenderNote = (conv) => {
      if (!conv) return conv;
      const cid = contactIdFromConversation(conv);
      if (Number(cid) !== Number(contactId)) return conv;
      const sender = { ...(conv.meta?.sender || {}) };
      const attrs = {
        ...(sender.custom_attributes || sender.customAttributes || {}),
        nota: note,
      };
      delete sender.customAttributes;
      sender.custom_attributes = attrs;
      return {
        ...conv,
        meta: {
          ...(conv.meta || {}),
          sender,
        },
      };
    };
    setSelectedConv((prev) => patchSenderNote(prev));
    setConversations((prev) => prev.map(patchSenderNote));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useLayoutEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 40)}px`;
  }, [draft, open, selectedId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (forwardNoteItem || closeOsItem || osChipMenu) return;
      if (openDropdown) {
        setOpenDropdown(null);
        return;
      }
      if (layoutLocked) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, openDropdown, layoutLocked, forwardNoteItem, closeOsItem, osChipMenu]);

  useEffect(() => {
    if (!openDropdown && !emojiOpen) return undefined;
    const onPointerDown = (e) => {
      if (e.target?.closest?.('.bch-dd')) return;
      if (e.target?.closest?.('.bch-emoji-picker') || e.target?.closest?.('.bch-composer-tool--emoji')) return;
      setOpenDropdown(null);
      setEmojiOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [openDropdown, emojiOpen]);

  useEffect(() => {
    setEmojiOpen(false);
  }, [selectedId]);

  const patchAttachmentDraft = useCallback((localId, patch) => {
    setAttachmentDrafts((prev) =>
      prev.map((item) => (item.localId === localId ? { ...item, ...patch } : item))
    );
  }, []);

  const removeAttachmentDraft = useCallback(async (localId) => {
    setAttachmentDrafts((prev) => {
      const target = prev.find((item) => item.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      if (target?.key && selectedId) {
        deleteChatAttachment(selectedId, target.key).catch(() => {});
      }
      return prev.filter((item) => item.localId !== localId);
    });
  }, [selectedId]);

  const uploadAttachmentFile = useCallback(
    async (localId, file, conversationId) => {
      const classified = classificarArquivo(file.name);
      if (!classified) {
        patchAttachmentDraft(localId, {
          status: 'error',
          error: 'Extensão não permitida',
        });
        return;
      }
      let working = file;
      if (classified.kind === 'foto') {
        working = await optimizePhotoFile(file);
      }
      const contentType =
        working.type || contentTypeParaExt(classificarArquivo(working.name)?.ext || classified.ext);
      patchAttachmentDraft(localId, {
        status: 'uploading',
        progress: 0,
        fileName: working.name,
        contentType,
        size: working.size,
        kind: classified.kind,
      });
      try {
        const presign = await presignChatAttachment(conversationId, {
          fileName: working.name,
          contentType,
          sizeBytes: working.size,
        });
        const { uploadUrl, key, contentType: signedCt } = presign.data || {};
        if (!uploadUrl || !key) throw new Error('Presign inválido');
        const putCt = signedCt || contentType;
        patchAttachmentDraft(localId, { key, contentType: putCt });
        await putFileToS3(uploadUrl, working, putCt, (progress) => {
          patchAttachmentDraft(localId, { progress });
        });
        patchAttachmentDraft(localId, { status: 'validating', progress: 100 });
        const completed = await completeChatAttachment(conversationId, {
          key,
          fileName: working.name,
          contentType: putCt,
          sizeBytes: working.size,
        });
        const meta = completed.data || {};
        patchAttachmentDraft(localId, {
          status: 'done',
          key: meta.key || key,
          attachment: meta,
          size: meta.size || working.size,
          contentType: meta.contentType || putCt,
          fileName: meta.filename || working.name,
          error: '',
        });
      } catch (e) {
        patchAttachmentDraft(localId, {
          status: 'error',
          error: e.message || 'Falha no upload',
        });
      }
    },
    [patchAttachmentDraft]
  );

  const handlePickAttachments = useCallback(
    async (fileList) => {
      if (!selectedId) {
        setError('Selecione uma conversa para anexar arquivos');
        return;
      }
      const files = Array.from(fileList || []);
      if (!files.length) return;
      const current = attachmentDrafts.filter((d) => d.status !== 'error').length;
      const room = Math.max(0, MAX_FILES_PER_DRAFT - current);
      if (room <= 0) {
        setError(`Máximo de ${MAX_FILES_PER_DRAFT} arquivos por vez`);
        return;
      }
      const batch = files.slice(0, room);
      const rejected = files.length - batch.length;
      if (rejected > 0) {
        setError(`Apenas ${MAX_FILES_PER_DRAFT} arquivos por vez (${rejected} ignorado(s))`);
      } else {
        setError('');
      }

      const created = [];
      for (const file of batch) {
        const classified = classificarArquivo(file.name);
        if (!classified) {
          setError('Extensão não permitida. Use foto (jpg, jpeg, jfif, png, heic) ou vídeo (mp4, mov, avi, mkv).');
          continue;
        }
        const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const previewUrl = URL.createObjectURL(file);
        created.push({
          localId,
          fileName: file.name,
          contentType: file.type || contentTypeParaExt(classified.ext),
          size: file.size,
          kind: classified.kind,
          status: 'pending',
          progress: 0,
          previewUrl,
          key: null,
          attachment: null,
          error: '',
          file,
        });
      }
      if (!created.length) return;
      setAttachmentDrafts((prev) => [...prev, ...created]);
      created.forEach((item) => {
        uploadAttachmentFile(item.localId, item.file, selectedId);
      });
    },
    [attachmentDrafts, selectedId, uploadAttachmentFile]
  );

  const voiceRecorder = useComposerVoiceRecorder({
    onError: (msg) => setError(msg || 'Falha no microfone'),
  });

  const handleVoiceNote = useCallback(async () => {
    if (!selectedId) {
      setError('Selecione uma conversa para gravar');
      await voiceRecorder.cancel();
      return;
    }
    const result = await voiceRecorder.finish();
    if (!result?.file) {
      setError('Gravação vazia');
      return;
    }
    const room =
      MAX_FILES_PER_DRAFT - attachmentDrafts.filter((d) => d.status !== 'error').length;
    if (room <= 0) {
      setError(`Máximo de ${MAX_FILES_PER_DRAFT} arquivos por vez`);
      return;
    }
    const localId = `${Date.now()}-voice`;
    const previewUrl = URL.createObjectURL(result.blob);
    setAttachmentDrafts((prev) => [
      ...prev,
      {
        localId,
        fileName: result.fileName,
        contentType: result.mime,
        size: result.file.size,
        kind: 'audio',
        status: 'pending',
        progress: 0,
        previewUrl,
        key: null,
        attachment: null,
        error: '',
        durationSec: result.durationSec,
        file: result.file,
      },
    ]);
    uploadAttachmentFile(localId, result.file, selectedId);
  }, [attachmentDrafts, selectedId, uploadAttachmentFile, voiceRecorder]);

  const handleVoiceTranscribe = useCallback(async () => {
    if (!selectedId) {
      setError('Selecione uma conversa para continuar');
      await voiceRecorder.cancel();
      return;
    }
    setError('');
    try {
      if (!voiceRecorder.speechSupported) {
        await voiceRecorder.cancel();
        setError('Não foi possível obter o texto desta gravação.');
        return;
      }
      const text = String((await voiceRecorder.finishTranscript()) || '').trim();
      if (!text) {
        setError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setDraft((prev) => (prev ? `${prev.trim()} ${text}` : text));
      if (!asPrivate) notifyAgentTyping();
    } catch (e) {
      setError(e.message || 'Não foi possível obter o texto desta gravação.');
    }
  }, [asPrivate, notifyAgentTyping, selectedId, voiceRecorder]);

  const reloadConversationOs = useCallback(async () => {
    const convId = selectedId || selectedConv?.id;
    if (!convId) return;
    try {
      const osRes = await fetchConversationOs(convId);
      applyConversationOsResponse(convId, osRes, { notifyUi: true });
    } catch {
      /* silencioso */
    }
  }, [selectedId, selectedConv?.id, applyConversationOsResponse]);

  const handleClaimUnassignedOs = useCallback(async (osId = null) => {
    const convId = selectedId || selectedConv?.id;
    if (!convId || claimUnassignedBusy) return;
    setClaimUnassignedBusy(true);
    setError('');
    try {
      const body =
        osId != null && String(osId).trim() !== ''
          ? { osId: String(osId).trim() }
          : {};
      await claimUnassignedConversationOs(convId, body);
      setOsChipMenu(null);
      hideTip();
      await reloadConversationOs();
      notifyConversationOsUpdated(convId, osId || null, 'claimed');
      onBadgesRefresh?.();
    } catch (e) {
      setError(e.message || 'Não foi possível assumir a OS');
      await reloadConversationOs();
    } finally {
      setClaimUnassignedBusy(false);
    }
  }, [
    selectedId,
    selectedConv?.id,
    claimUnassignedBusy,
    reloadConversationOs,
    onBadgesRefresh,
    hideTip,
  ]);

  const handleAssumeAttendance = useCallback(async () => {
    const convId = selectedId || selectedConv?.id;
    if (!convId || assumeAttendanceBusy) return;
    setAssumeAttendanceBusy(true);
    setError('');
    try {
      await assignConversationAgent(convId);
      const [refreshed, osRefresh] = await Promise.all([
        fetchConversation(convId),
        fetchConversationOs(convId).catch(() => null),
      ]);
      if (osRefresh) {
        applyConversationOsResponse(convId, osRefresh, { notifyUi: true });
      }
      const conv = refreshed.data || null;
      if (conv) {
        setSelectedConv((prev) => {
          if (!prev || Number(prev.id) !== Number(convId)) {
            return {
              ...conv,
              status: conversationStatusOf(conv) || conv.status || prev?.status,
            };
          }
          return {
            ...prev,
            ...conv,
            status:
              conversationStatusOf(conv) ||
              conversationStatusOf(prev) ||
              prev.status,
          };
        });
        patchConversationInList({
          id: Number(convId),
          meta: conv.meta,
          assignee: conv.assignee,
          assigneeVerveUserId: conv.assigneeVerveUserId,
          hasAssigneeProfilePhoto: conv.hasAssigneeProfilePhoto,
          ...(conversationStatusOf(conv)
            ? { status: conversationStatusOf(conv) }
            : null),
        });
      }
      onBadgesRefresh?.();
    } catch (e) {
      setError(e.message || 'Não foi possível assumir o atendimento');
    } finally {
      setAssumeAttendanceBusy(false);
    }
  }, [
    selectedId,
    selectedConv?.id,
    assumeAttendanceBusy,
    applyConversationOsResponse,
    patchConversationInList,
    onBadgesRefresh,
  ]);

  const handleResumeSnoozedComposerOs = useCallback(async () => {
    const id = String(snoozedOsReplyPromptOsId || composerOsId || '').trim();
    const convId = selectedId || selectedConv?.id;
    if (!id || !convId || snoozedOsResumeBusy) return;
    setSnoozedOsResumeBusy(true);
    setError('');
    try {
      await patchConversationOs(convId, id, { status: 'pending' });
      setSnoozedOsReplyPromptOsId(null);
      await reloadConversationOs();
      notifyConversationOsUpdated(convId, id, 'pending');
      onBadgesRefresh?.();
      setAsPrivate(false);
    } catch (e) {
      setError(e.message || 'Falha ao retomar OS');
    } finally {
      setSnoozedOsResumeBusy(false);
    }
  }, [
    snoozedOsReplyPromptOsId,
    composerOsId,
    selectedId,
    selectedConv?.id,
    snoozedOsResumeBusy,
    reloadConversationOs,
    onBadgesRefresh,
  ]);

  useEffect(() => {
    const onOsUpdated = (e) => {
      const cid = e?.detail?.conversationId;
      const current = selectedId || selectedConv?.id;
      if (!cid || !current || String(cid) !== String(current)) return;
      reloadConversationOs();
    };
    window.addEventListener(BCH_CONVERSATION_OS_UPDATED, onOsUpdated);
    return () => window.removeEventListener(BCH_CONVERSATION_OS_UPDATED, onOsUpdated);
  }, [selectedId, selectedConv?.id, reloadConversationOs]);

  const listTeam =
    tenantTeams.find((t) => Number(t.id) === Number(teamId)) ||
    teams.find((t) => Number(t.id) === Number(teamId));
  const isListPreAtendimento = Boolean(
    listTeam?.isPreAtendimento ||
      /pr[eé][\s-]atendimento/i.test(listTeam?.name || '')
  );

  conversationsRef.current = conversations;
  isListPreAtendimentoRef.current = isListPreAtendimento;

  // Sync cross-agente: handoff/snooze + chips/lista sem depender só do cable
  useEffect(() => {
    if (!open) return undefined;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      loadConversations({ silent: true });
      const selected = selectedIdRef.current;
      if (selected) {
        fetchConversationOs(selected)
          .then((osRes) => {
            if (Number(selectedIdRef.current) !== Number(selected)) return;
            applyConversationOsResponse(selected, osRes, { notifyUi: true });
          })
          .catch(() => {});
      }
      if (isListPreAtendimentoRef.current) {
        const ids = (conversationsRef.current || [])
          .map((c) => Number(c?.id))
          .filter((id) => Number.isFinite(id) && id > 0);
        ids.forEach((cid) => {
          fetchConversationOs(cid)
            .then((osRes) => {
              const list = parseConversationOsResponse(osRes).items;
              setOsByConvId((prev) => ({ ...prev, [cid]: list }));
            })
            .catch(() => {});
        });
      }
      if (!suppressBadgesUntilMarkReadRef.current) onBadgesRefresh?.();
    };
    const id = window.setInterval(tick, 12000);
    return () => window.clearInterval(id);
  }, [open, loadConversations, onBadgesRefresh, applyConversationOsResponse]);

  useEffect(() => {
    osPrefetchDoneRef.current = new Set();
    setOsByConvId({});
  }, [teamId]);

  useEffect(() => {
    if (!open || !isListPreAtendimento || !Array.isArray(conversations) || conversations.length === 0) {
      return undefined;
    }
    let cancelled = false;
    const queue = conversations
      .map((c) => Number(c?.id))
      .filter((id) => Number.isFinite(id) && !osPrefetchDoneRef.current.has(id));

    const run = async () => {
      for (const cid of queue) {
        if (cancelled) return;
        osPrefetchDoneRef.current.add(cid);
        try {
          const osRes = await fetchConversationOs(cid);
          if (cancelled) return;
          const list = parseConversationOsResponse(osRes).items;
          setOsByConvId((prev) => ({ ...prev, [cid]: list }));
        } catch {
          /* silencioso */
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, isListPreAtendimento, conversations, teamId]);

  useEffect(() => {
    if (!open || !isListPreAtendimento || !teamId) {
      setPreAtendHandoffAgents([]);
      return undefined;
    }
    let cancelled = false;
    fetchTeamAgents(teamId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setPreAtendHandoffAgents(list);
      })
      .catch(() => {
        if (!cancelled) setPreAtendHandoffAgents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isListPreAtendimento, teamId]);

  useEffect(() => {
    setPendingOsHolder(null);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || !osHandoffActive) return;
    const holderId = getPendingOsHolderAgentId(conversationOsList);
    const me = Number(session?.chatwootUserId);
    const blockingOsIds = getBlockingPendingOsIds(conversationOsList);
    if (!holderId || !Number.isFinite(me) || holderId === me || !blockingOsIds.length) {
      setPendingOsHolder(null);
      return;
    }
    const agent = (preAtendHandoffAgents || []).find(
      (a) => Number(a?.id ?? a?.user_id) === Number(holderId)
    );
    setPendingOsHolder({
      id: holderId,
      name: agent?.name || agent?.available_name || `agente #${holderId}`,
      osIds: blockingOsIds,
    });
  }, [
    selectedId,
    osHandoffActive,
    conversationOsList,
    session?.chatwootUserId,
    preAtendHandoffAgents,
  ]);

  useEffect(() => {
    if (pendingOsHolder) {
      pendingOsHolderWasActiveRef.current = true;
      setAsPrivate(true);
      return;
    }
    // Liberou a fila: volta de Anotação interna → Resposta automaticamente
    // (exceto se a OS do composer estiver encaminhada ou adiada).
    if (pendingOsHolderWasActiveRef.current) {
      pendingOsHolderWasActiveRef.current = false;
      const st = String(
        composerOsOptions.find((o) => o.osId === composerOsId)?.status || ''
      ).toLowerCase();
      if (st !== 'forwarded' && st !== 'snoozed') setAsPrivate(false);
    }
    setError((prev) =>
      /Aguardando .+ finalizar/i.test(String(prev || '')) ? '' : prev
    );
  }, [pendingOsHolder, composerOsId, composerOsOptions]);

  /** Enquanto bloqueado pelo holder, revalida OS para liberar assim que sair de pendente. */
  useEffect(() => {
    if (!pendingOsHolder || !selectedId) return undefined;
    const tick = () => {
      reloadConversationOs();
    };
    const id = window.setInterval(tick, 8000);
    return () => window.clearInterval(id);
  }, [pendingOsHolder, selectedId, reloadConversationOs]);

  const handleSelectConvOs = useCallback(
    (convId, osId) => {
      const id = String(osId || '').trim();
      if (!id) return;
      if (Number(selectedId) === Number(convId)) {
        setComposerOsId(id);
        return;
      }
      pendingComposerOsIdRef.current = id;
      setSelectedId(Number(convId));
    },
    [selectedId]
  );

  const finishReturnToQueue = useCallback(
    async ({ handoffToAgentId = null, osIdsToHandoff = [] } = {}) => {
      if (!selectedId || returningToQueue || statusBusy) return;
      if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList)) {
        return;
      }
      const convId = Number(selectedId);
      const me = Number(session?.chatwootUserId);
      const owned = getOsOwnedByAgent(conversationOsList, me);
      const ownedIds = owned
        .map((item) => String(item?.osId || item?.os_id || '').trim())
        .filter(Boolean);
      const ids =
        Array.isArray(osIdsToHandoff) && osIdsToHandoff.length
          ? osIdsToHandoff.map((id) => String(id).trim()).filter(Boolean)
          : ownedIds;

      setReturningToQueue(true);
      setError('');
      setReturnQueuePicker(null);
      try {
        if (handoffToAgentId && ids.length) {
          for (const osId of ids) {
            await handoffConversationOs(convId, osId, handoffToAgentId, {
              claimUnassigned: false,
              syncChatwootAssignee: false,
            });
          }
        } else if (ownedIds.length) {
          await clearConversationOsAssignee(convId);
        }

        await unassignConversationAgent(convId);
        patchConversationInList({
          id: convId,
          clearAssignee: true,
        });
        setThreadLeaving(true);
        await new Promise((r) => window.setTimeout(r, 160));
        clearThreadSelection();
        setThreadLeaving(false);
        await loadConversations({ silent: true });
        onBadgesRefresh?.();
      } catch (e) {
        setThreadLeaving(false);
        setError(e.message || 'Falha ao devolver conversa para a fila');
      } finally {
        setReturningToQueue(false);
      }
    },
    [
      selectedId,
      returningToQueue,
      statusBusy,
      selectedConv,
      session?.chatwootUserId,
      conversationOsList,
      patchConversationInList,
      loadConversations,
      onBadgesRefresh,
      clearThreadSelection,
    ]
  );

  /** Sai da thread sem Resolver — OS de outros permanecem; conversa segue aberta. */
  const handleLeaveAttendance = useCallback(async () => {
    if (!selectedId || returningToQueue || statusBusy) return;
    if (!canLeaveAttendanceForPeerOs(conversationOsList, session?.chatwootUserId)) return;
    const convId = Number(selectedId);
    const me = Number(session?.chatwootUserId);
    const currentAssigneeId = Number(
      assigneeFromConversation(selectedConv)?.id ?? selectedConv?.meta?.assignee?.id
    );
    setReturningToQueue(true);
    setError('');
    try {
      if (Number.isFinite(me) && me > 0 && currentAssigneeId === me) {
        await unassignConversationAgent(convId);
        patchConversationInList({ id: convId, clearAssignee: true });
      }
      setThreadLeaving(true);
      await new Promise((r) => window.setTimeout(r, 160));
      clearThreadSelection();
      setThreadLeaving(false);
      await loadConversations({ silent: true });
      onBadgesRefresh?.();
    } catch (e) {
      setThreadLeaving(false);
      setError(e.message || 'Falha ao sair do atendimento');
    } finally {
      setReturningToQueue(false);
    }
  }, [
    selectedId,
    returningToQueue,
    statusBusy,
    conversationOsList,
    session?.chatwootUserId,
    selectedConv,
    patchConversationInList,
    loadConversations,
    onBadgesRefresh,
    clearThreadSelection,
  ]);

  const handleReturnToQueue = useCallback(
    (e) => {
      if (!selectedId || returningToQueue || statusBusy) return;
      if (isConversationInteractionLocked(selectedConv, session?.chatwootUserId, conversationOsList)) {
        return;
      }
      const me = Number(session?.chatwootUserId);
      const owned = getOsOwnedByAgent(conversationOsList, me);
      const othersHave = conversationHasOsOwnedByOtherAgent(conversationOsList, me);
      const peers = (preAtendHandoffAgents || []).filter((a) => {
        const id = Number(a?.id ?? a?.user_id);
        return Number.isFinite(id) && id > 0 && id !== me;
      });

      // Já há OS com outros agentes → pedir para quem repassar as suas antes de sair.
      if (owned.length && othersHave && peers.length) {
        const btn = e?.currentTarget;
        const rect = btn?.getBoundingClientRect?.();
        setReturnQueuePicker({
          rect: rect
            ? {
                left: rect.left,
                right: rect.right,
                top: rect.top,
                bottom: rect.bottom,
              }
            : null,
          osIds: owned
            .map((item) => String(item?.osId || item?.os_id || '').trim())
            .filter(Boolean),
        });
        return;
      }

      finishReturnToQueue();
    },
    [
      selectedId,
      returningToQueue,
      statusBusy,
      selectedConv,
      session?.chatwootUserId,
      conversationOsList,
      preAtendHandoffAgents,
      finishReturnToQueue,
    ]
  );

  if (!open) return null;

  const contactName = contactNameFromConversation(selectedConv);
  const chatContactId = contactIdFromConversation(selectedConv);
  const chatContactNote = contactNoteFromConversation(selectedConv);
  const assignee = assigneeFromConversation(selectedConv);
  const currentTeamId = teamIdFromConversation(selectedConv) || teamId;
  const currentTeam =
    tenantTeams.find((t) => Number(t.id) === Number(currentTeamId)) ||
    teams.find((t) => Number(t.id) === Number(currentTeamId));
  const teamLabel =
    teamNameFromConversation(selectedConv, teams) ||
    teams.find((t) => Number(t.id) === Number(teamId))?.name ||
    '—';
  const contratos = Array.isArray(portalCustomer?.contratos) ? portalCustomer.contratos : [];
  const contratoLabel =
    contratos.length === 0 ? '—' : contratos.length === 1 ? contratos[0] : 'Multiplos';
  const isGrupo = portalCustomer && !portalCustomer.isSolo;
  const clienteLabel = isGrupo ? 'Grupo' : 'Cliente';
  const clienteValue = isGrupo
    ? portalCustomer?.nomeGrupo || '—'
    : portalCustomer?.nomeCliente || contactName || '—';
  const usuarioEmail = portalCustomer?.email || '—';
  const cargoValue = portalCustomer?.cargo ? portalCustomer.cargo : '—';
  const foneFixoDisplay = formatPhoneDisplay(portalCustomer?.foneFixo) || '—';
  const foneWhatsDisplay = formatPhoneDisplay(portalCustomer?.foneWhatsapp) || '—';
  const activeFilter = STATUS_FILTERS.find((f) => f.id === statusFilter) || STATUS_FILTERS[0];
  const openCount = meta.all_count ?? meta.mine_count ?? conversations.length;
  const statusFilterCounts = {
    active: inboxBuckets.nova + inboxBuckets.pending + inboxBuckets.open,
    unattended: inboxBuckets.nova,
    pending: inboxBuckets.pending,
    open: inboxBuckets.open,
  };
  const unreadMessagesTotal = activeInboxCount;
  const cannedQuery = cannedShortcutQuery(draft);
  const cannedSuggestions = filterCannedByShortcut(cannedQuery, cannedResponses);
  const showCannedSuggestions = cannedQuery != null && cannedResponses.length > 0;
  const sortedConversations = applyResolvedFilters(
    [...conversations].sort((a, b) => {
      const ta = Number(a.last_activity_at || a.timestamp || 0);
      const tb = Number(b.last_activity_at || b.timestamp || 0);
      return sortOrder === 'desc' ? tb - ta : ta - tb;
    }),
    statusFilter === 'resolved' ? resolvedFilters : EMPTY_RESOLVED_FILTERS
  );

  const resolvedFiltersCount = countResolvedFilters(resolvedFilters);

  const handleExportResolved = async () => {
    if (resolvedFiltersCount === 0 || exportingResolved) return;
    setExportingResolved(true);
    try {
      await exportResolvedConversationsXlsx({
        conversations: sortedConversations,
        filters: resolvedFilters,
        teams,
      });
    } catch (e) {
      window.alert(e.message || 'Não foi possível exportar as conversas.');
    } finally {
      setExportingResolved(false);
    }
  };
  const groupConversationsByClient =
    Boolean(session?.groupConversationsByClient) ||
    (Array.isArray(conversations) &&
      conversations.some((c) => c?.clientGroupKey != null && String(c.clientGroupKey).trim() !== ''));
  const clientGroupedList = groupConversationsByClient
    ? (() => {
        const map = new Map();
        for (const c of sortedConversations) {
          const key = String(c?.clientGroupKey || 'unknown');
          const label = String(c?.clientGroupLabel || 'Sem cliente').trim() || 'Sem cliente';
          if (!map.has(key)) map.set(key, { id: key, label, items: [] });
          map.get(key).items.push(c);
        }
        return [...map.values()].sort((a, b) =>
          a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
        );
      })()
    : null;
  const groupedConversations =
    !groupConversationsByClient && statusFilter === 'active'
      ? {
          unattended: sortedConversations.filter(
            (c) => String(c.status || '').toLowerCase() === 'unattended'
          ),
          pending: sortedConversations.filter(
            (c) => String(c.status || '').toLowerCase() === 'pending'
          ),
          open: sortedConversations.filter((c) => String(c.status || '').toLowerCase() === 'open'),
        }
      : null;
  const visibleListGroups = groupedConversations
    ? LIST_GROUPS.filter((group) => (groupedConversations[group.id] || []).length > 0)
    : [];
  const visibleClientGroups = clientGroupedList || [];
  const useGroupedList = Boolean(clientGroupedList) || Boolean(groupedConversations);
  const convStatusNorm = conversationStatusOf(selectedConv);
  const isResolvedConversation = convStatusNorm === 'resolved';
  /** OS do agente já encerrada/encaminhada; peer ainda ativo → só leitura. */
  const osDoneReadOnly = isConversationReadOnlyForAgent(
    conversationOsList,
    session?.chatwootUserId
  );
  /** Fila / conversa nova sem assignee — visualização até Assumir atendimento. */
  const awaitingAssumeAttendance = isConversationAwaitingAssume(
    selectedConv,
    session?.chatwootUserId,
    conversationOsList,
    { isPreAtendimento }
  );
  /** Só visualização por assignee — após 1º handoff de OS, vale a trava do holder. */
  const interactionLocked =
    isConversationInteractionLocked(
      selectedConv,
      session?.chatwootUserId,
      conversationOsList
    ) || osDoneReadOnly;
  /** Trava UI (composer, detalhes, pills) — inclui aguardando assume. */
  const actionsLocked = interactionLocked || awaitingAssumeAttendance;
  const composerLocked = isResolvedConversation || actionsLocked;
  const meChatwootId = Number(session?.chatwootUserId);
  const handoffAgents = (preAtendHandoffAgents || []).filter((a) => {
    const id = Number(a?.id ?? a?.user_id);
    return Number.isFinite(id) && id > 0 && id !== meChatwootId;
  });
  const replyBlockedByOsHolder = Boolean(pendingOsHolder) && !asPrivate;
  const holderOsIds = pendingOsHolder?.osIds || [];
  const holderOsLabel = formatOsIdListLabel(holderOsIds);
  const holderPendingWord = holderOsIds.length > 1 ? 'pendentes' : 'pendente';
  const holderWaitMessage = pendingOsHolder
    ? `Aguardando ${pendingOsHolder.name}${
        holderOsLabel
          ? ` finalizar ${holderOsLabel} ${holderPendingWord}`
          : ' finalizar OS pendente'
      } — use anotação interna ou aguarde.`
    : '';
  const composerSendLocked = composerLocked || replyBlockedByOsHolder;
  const canReopenConversation = convStatusNorm === 'pending' || isResolvedConversation;
  const primaryAction = isResolvedConversation ? 'open' : 'resolved';
  const threadActionsBusy =
    Boolean(statusBusy) || returningToQueue || assumeAttendanceBusy;
  const pendingOsForResolve = (conversationOsList || []).filter((item) => {
    const id = String(item?.osId || item?.os_id || '').trim();
    if (!id) return false;
    // Só vínculo rastreável (vc_chat_conversation_os) — fallback de mensagem não bloqueia.
    if (item?.trackable === false) return false;
    const st = String(item?.status || 'pending').toLowerCase();
    return st !== 'forwarded' && st !== 'closed';
  });
  const resolveBlockedByPendingOs =
    isPreAtendimento && pendingOsForResolve.length > 0;
  const peerPendingOsList = getPendingOsOwnedByOtherAgents(
    conversationOsList,
    session?.chatwootUserId
  );
  const canLeaveAttendance = Boolean(
    isPreAtendimento &&
      !isResolvedConversation &&
      !awaitingAssumeAttendance &&
      canLeaveAttendanceForPeerOs(conversationOsList, session?.chatwootUserId)
  );
  const peerOsAgentLookup = [
    ...(preAtendHandoffAgents || []),
    ...(assignee?.id ? [{ id: assignee.id, name: assignee.name }] : []),
  ];
  const peerOsTipRows = buildOsAssignmentTipRows(peerPendingOsList, peerOsAgentLookup);
  const peerOsBannerText = (() => {
    if (!peerOsTipRows.length) return '';
    if (peerOsTipRows.length === 1) {
      const row = peerOsTipRows[0];
      return `${row.label} com ${row.value.replace(/^@/, '')} — a conversa permanece aberta.`;
    }
    const bits = peerOsTipRows.map((r) => `${r.label} (${r.value.replace(/^@/, '')})`);
    return `OS com outros atendentes: ${bits.join('; ')} — a conversa permanece aberta.`;
  })();
  const resolveDisabled = awaitingAssumeAttendance
    ? threadActionsBusy
    : threadActionsBusy ||
      isResolvedConversation ||
      interactionLocked ||
      resolveBlockedByPendingOs;
  const resolveAssignmentTipRows = buildOsAssignmentTipRows(
    pendingOsForResolve,
    peerOsAgentLookup
  );
  const resolveBlockedTip = awaitingAssumeAttendance
    ? null
    : interactionLocked
      ? {
          title: 'Somente visualização',
          rows: osDoneReadOnly
            ? [
                {
                  label: 'Motivo',
                  value:
                    'Sua OS já foi encaminhada ou fechada. Outra OS desta conversa segue com outro atendente.',
                },
                {
                  label: 'Como liberar',
                  value: 'Você volta a editar se receber uma OS via repasse.',
                },
              ]
            : [
                {
                  label: 'Motivo',
                  value: assignee?.name
                    ? `Em atendimento por @${assignee.name}.`
                    : 'Conversa atribuída a outro atendente.',
                },
                {
                  label: 'Como liberar',
                  value: 'O atendente deve devolver o chat para a fila.',
                },
              ],
        }
      : resolveBlockedByPendingOs
        ? {
            title: 'Resolver indisponível',
            rows: [
              ...(resolveAssignmentTipRows.length
                ? resolveAssignmentTipRows
                : [
                    {
                      label: 'Motivo',
                      value:
                        pendingOsForResolve.length === 1
                          ? 'Há 1 OS pendente neste pré-atendimento.'
                          : `Há ${pendingOsForResolve.length} OS(s) pendente(s) neste pré-atendimento.`,
                    },
                  ]),
              {
                label: 'Como liberar',
                value: 'Encaminhe ou feche todas as OS vinculadas antes de resolver.',
              },
            ],
          }
        : null;
  const isThreadMismatch =
    selectedId != null &&
    (messagesForConvId == null || Number(messagesForConvId) !== Number(selectedId));
  /** Troca de conversa: skeleton imediato (sem flash da thread anterior) */
  const showThreadSkeleton = isThreadMismatch;

  const sessionEntries = sessions.map((s, idx) => ({ s, idx }));
  const ongoingSessionEntries = sessionEntries.filter(({ s }) => s.endedAt == null);
  const pastSessionEntries = [...sessionEntries.filter(({ s }) => s.endedAt != null)].reverse();
  const visiblePastSessionEntries = showPastSessions
    ? pastSessionEntries.slice(0, pastSessionsVisible)
    : [];

  const renderSessionItem = ({ s, idx }) => {
    const ongoing = s.endedAt == null;
    const contestedUnclaimed = ongoing
      ? listContestedUnassignedOs(conversationOsList)
      : [];
    return (
      <div
        key={`${s.startedAt}-${idx}`}
        role="button"
        tabIndex={0}
        className={[
          'bch-session',
          'bch-session--compact',
          activeSessionIdx === idx ? 'is-active' : '',
          ongoing ? 'is-ongoing' : '',
          contestedUnclaimed.length ? 'has-unclaimed-os' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => scrollToSession(s, idx)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scrollToSession(s, idx);
          }
        }}
      >
        <span className="bch-session-dot" aria-hidden="true" />
        <span className="bch-session-main">
          <span className="bch-session-line">
            <span className="bch-session-status">
              {ongoing ? 'Em andamento' : sessionStatusLabel(s.status)}
            </span>
            <span className="bch-session-range">
              {sessionTimeLabel(s.startedAt)}
              {' → '}
              {ongoing ? 'agora' : sessionTimeLabel(s.endedAt)}
            </span>
            {ongoing && selectedId != null ? (
              <span className="bch-session-protocol">#protocolo {selectedId}</span>
            ) : null}
          </span>
          {ongoing ? (
            <BoardChatUnclaimedOsHint
              count={contestedUnclaimed.length}
              busy={claimUnassignedBusy}
              onClaim={() => handleClaimUnassignedOs()}
            />
          ) : null}
        </span>
      </div>
    );
  };

  const renderConversationItem = (c) => {
    const name = contactNameFromConversation(c);
    const unreadCount = Number(c.unread_count) || 0;
    const unread = unreadCount > 0;
    const isNew = c.first_reply_created_at == null;
    const assignee = assigneeFromConversation(c);
    const ts =
      c.last_non_activity_message?.created_at || c.timestamp || c.last_activity_at;
    const dragging = Number(dragConvId) === Number(c.id);
    const convResolved = conversationStatusOf(c) === 'resolved';
    const convActive = Number(selectedId) === Number(c.id);
    const fullOsList = convActive
      ? conversationOsList
      : osByConvId[Number(c.id)] || [];
    const convInteractionLocked =
      isConversationInteractionLocked(c, session?.chatwootUserId, fullOsList) ||
      isConversationReadOnlyForAgent(fullOsList, session?.chatwootUserId);
    const dragDisabled = transferringTeam || convResolved || convInteractionLocked;
    const convOsList = filterOsListForAgent(fullOsList, session?.chatwootUserId).map(
      (item) => ({
        ...item,
        unclaimed: isContestedUnassignedOs(item, fullOsList),
      })
    );
    const handoffOnConv = conversationOsHandoffActive(fullOsList);
    const osAssignmentTip =
      handoffOnConv
        ? {
            rows: buildOsAssignmentTipRows(fullOsList, [
              ...(preAtendHandoffAgents || []),
              ...(assignee?.id
                ? [{ id: assignee.id, name: assignee.name }]
                : []),
            ]),
          }
        : null;
    const showOsChips =
      isListPreAtendimento && Array.isArray(convOsList) && convOsList.length >= 1;
    return (
      <div
        key={c.id}
        className={[
          'bch-conv-item',
          showOsChips ? 'has-os-chips' : '',
          convActive ? 'is-active' : '',
          unread ? 'is-unread' : '',
          isNew ? 'is-new' : '',
          dragging ? 'is-dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        draggable={!dragDisabled}
        onDragStart={(e) => {
          if (dragDisabled) {
            e.preventDefault();
            return;
          }
          setDragConvId(c.id);
          e.dataTransfer.setData('text/plain', String(c.id));
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          setDragConvId(null);
          setDropTeamId(null);
        }}
      >
        <div className="bch-conv-item-main">
          <div className="bch-conv-item-row">
            <button
              type="button"
              className="bch-conv"
              onClick={() => setSelectedId(c.id)}
            >
              <Avatar
                name={name}
                size={27}
                photoUrl={contactPhotoByConv[c.id]}
                assignee={assignee}
                tipProps={tipProps}
                tipExtra={osAssignmentTip}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="bch-conv-title-row">
                  <p className="bch-conv-name">{name}</p>
                  {isNew ? <span className="bch-conv-pill-new">Nova</span> : null}
                  {unread ? (
                    <span className="bch-conv-unread-badge" title={`${unreadCount} não lida(s)`}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  ) : null}
                </div>
                <div className="bch-conv-row">
                  {lastMessageIsFiles(c) ? (
                    <p className="bch-conv-preview bch-conv-preview--files">
                      <Paperclip size={12} strokeWidth={1.85} aria-hidden />
                      <span>Arquivos</span>
                    </p>
                  ) : (
                    <p className="bch-conv-preview">{lastMessagePreview(c)}</p>
                  )}
                  <span className="bch-conv-time">{relativeTime(ts)}</span>
                </div>
              </div>
            </button>
            <button
              type="button"
              className="bch-conv-unread-toggle"
              aria-label="Marcar como não lida"
              disabled={unread}
              {...tipProps('Marcar como não lida')}
              onClick={() => {
                hideTip();
                if (unread) return;
                markConversationUnread(c.id)
                  .then(() => {
                    patchConversationInList({
                      id: Number(c.id),
                      unread_count: Math.max(1, unreadCount || 1),
                    });
                    onBadgesRefresh?.();
                  })
                  .catch(() => {});
              }}
            >
              <MailOpen size={13} strokeWidth={1.8} />
            </button>
          </div>
          {showOsChips ? (
            <BoardChatConvOsChips
              osList={convOsList}
              agents={[
                ...(preAtendHandoffAgents || []),
                ...(assignee?.id ? [{ id: assignee.id, name: assignee.name }] : []),
              ]}
              activeOsId={convActive ? composerOsId : null}
              conversationActive={convActive}
              onSelectOs={(osId) => handleSelectConvOs(c.id, osId)}
              tipProps={tipProps}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const insertEmoji = (emoji) => {
    const el = draftRef.current;
    if (!el) {
      setDraft((v) => `${v}${emoji}`);
      if (!asPrivate) notifyAgentTyping();
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = `${draft.slice(0, start)}${emoji}${draft.slice(end)}`;
    setDraft(next);
    if (!asPrivate) notifyAgentTyping();
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const applyCannedContent = (content) => {
    const text = String(content || '');
    setDraft(text);
    if (!asPrivate) notifyAgentTyping();
    requestAnimationFrame(() => {
      const el = draftRef.current;
      if (!el) return;
      el.focus();
      const pos = text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSend = async () => {
    const content = draft.trim();
    const readyAttachments = attachmentDrafts.filter((d) => d.status === 'done' && d.attachment);
    const busy = attachmentDrafts.some(
      (d) => d.status === 'uploading' || d.status === 'validating' || d.status === 'pending'
    );
    if (busy) {
      setError('Aguarde o envio dos anexos');
      return;
    }
    if ((!content && !readyAttachments.length) || !selectedId || sending) return;
    if (actionsLocked) return;
    if (composerOsIsSnoozed && !asPrivate) {
      setAsPrivate(true);
      setSnoozedOsReplyPromptOsId(String(composerOsId || '').trim() || null);
      return;
    }
    if (composerOsIsForwarded && !asPrivate) {
      setAsPrivate(true);
      setForwardedOsReplyNoticeOsId(String(composerOsId || '').trim() || null);
      return;
    }
    if (pendingOsHolder && !asPrivate) {
      setError(
        holderWaitMessage ||
          `Aguardando ${pendingOsHolder.name || 'outro atendente'} finalizar OS pendente nessa conversa`
      );
      return;
    }
    const convId = Number(selectedId);
    stopAgentTyping();
    setSending(true);
    setError('');

    const makeOptimistic = (body) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      content: body,
      message_type: 1,
      private: Boolean(asPrivate),
      created_at: new Date().toISOString(),
      ...(composerOsId
        ? { content_attributes: { os_id: String(composerOsId) } }
        : null),
      _optimistic: true,
    });

    const sendPayload = (body) => {
      const payload = { content: body, private: asPrivate };
      if (composerOsId) payload.osId = String(composerOsId);
      return sendChatMessage(convId, payload);
    };

    setDraft('');

    try {
      if (readyAttachments.length) {
        for (let i = 0; i < readyAttachments.length; i += 1) {
          const item = readyAttachments[i];
          let body = buildAttachmentContent(item.attachment);
          // Legenda vai no 1º anexo → uma única mensagem Chatwoot (mesmo padrão do Customers).
          if (content && i === 0) body = `${body}\n${content}`;
          const optimistic = makeOptimistic(body);
          setMessages((prev) => [...prev, optimistic]);
          setMessagesForConvId(convId);
          await sendPayload(body);
        }
      } else if (content) {
        const optimistic = makeOptimistic(content);
        setMessages((prev) => [...prev, optimistic]);
        setMessagesForConvId(convId);
        await sendPayload(content);
      }
      setAttachmentDrafts((prev) => {
        prev.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        return [];
      });
      await loadThread(convId, { silent: true, markRead: false });
      await loadConversations({ silent: true });
      onBadgesRefresh?.();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => !m._optimistic));
      setDraft(content);
      const holderBlocked =
        e?.status === 403 &&
        (e?.error === 'another agent still has pending OS on this conversation' ||
          /another agent still has pending OS/i.test(String(e?.message || '')));
      if (holderBlocked) {
        const hid = e.holderId ?? e.payload?.holderId ?? null;
        const agent = (preAtendHandoffAgents || []).find(
          (a) => Number(a?.id ?? a?.user_id) === Number(hid)
        );
        const name =
          String(agent?.name || agent?.available_name || agent?.email || '').trim() ||
          (hid != null ? `atendente #${hid}` : 'outro atendente');
        const osIds = getBlockingPendingOsIds(conversationOsList);
        setPendingOsHolder({ id: hid, name, osIds });
        setAsPrivate(true);
        const osLabel = formatOsIdListLabel(osIds);
        const pendingWord = osIds.length > 1 ? 'pendentes' : 'pendente';
        setError(
          `Aguardando ${name}${
            osLabel ? ` finalizar ${osLabel} ${pendingWord}` : ' finalizar OS pendente'
          } nessa conversa`
        );
      } else {
        setError(e.message || 'Falha ao enviar');
      }
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (status) => {
    if (!selectedId || statusBusy || returningToQueue) return;
    if (actionsLocked) return;
    const convId = Number(selectedId);
    const prevStatus = conversationStatusOf(selectedConv) || selectedConv?.status;
    const nextStatus = String(status || '').trim().toLowerCase();
    setStatusBusy(nextStatus);
    setError('');
    setSelectedConv((prev) => (prev ? { ...prev, status: nextStatus } : prev));
    patchConversationInList({ id: convId, status: nextStatus });
    try {
      await updateChatStatus(convId, nextStatus);
      // Resolveu fora do filtro Resolvidas: sai da lista ativa — limpa thread/detalhes.
      const leavesCurrentList =
        nextStatus === 'resolved' && statusFilter !== 'resolved';
      if (leavesCurrentList) {
        setThreadLeaving(true);
        await new Promise((r) => window.setTimeout(r, 160));
        clearThreadSelection();
        setThreadLeaving(false);
      } else {
        // Revalida no Chatwoot (fonte da verdade) — não confiar só no otimista.
        await loadThread(convId, { silent: true, markRead: false });
      }
      await loadConversations({ silent: true });
      onBadgesRefresh?.();
    } catch (e) {
      setThreadLeaving(false);
      setSelectedConv((prev) => (prev ? { ...prev, status: prevStatus } : prev));
      if (prevStatus != null && prevStatus !== '') {
        patchConversationInList({ id: convId, status: prevStatus });
      }
      setError(e.message || 'Falha ao atualizar status');
    } finally {
      setStatusBusy(null);
    }
  };

  const handleConsultOsSituacao = () => {
    if (!validOsNumber) return;
    hideTip();
    if (typeof onOpenOrderDetails !== 'function') {
      setError(`Não foi possível abrir o detalhe da OS ${validOsNumber}.`);
      return;
    }
    const osNumber = validOsNumber;
    const pre = isPreAtendimento;
    window.setTimeout(() => {
      onOpenOrderDetails(osNumber, { isPreAtendimento: pre });
    }, 0);
  };

  const handleTransferTeam = async (conversationId, nextTeamId) => {
    const convId = Number(conversationId);
    const destId = Number(nextTeamId);
    if (!convId || !destId || transferringTeam) return;
    const convForTransfer =
      conversations.find((c) => Number(c.id) === convId) || selectedConv;
    const osListForTransfer =
      Number(selectedId) === convId
        ? conversationOsList
        : osByConvId[convId] || [];
    const transferTeamId = teamIdFromConversation(convForTransfer);
    const transferTeam =
      tenantTeams.find((t) => Number(t.id) === Number(transferTeamId)) ||
      teams.find((t) => Number(t.id) === Number(transferTeamId));
    const transferIsPre = Boolean(
      transferTeam?.isPreAtendimento ||
        /pr[eé][\s-]atendimento/i.test(transferTeam?.name || '') ||
        (osListForTransfer && osListForTransfer.length > 0)
    );
    if (
      isConversationInteractionLocked(
        convForTransfer,
        session?.chatwootUserId,
        osListForTransfer
      ) ||
      isConversationReadOnlyForAgent(osListForTransfer, session?.chatwootUserId) ||
      isConversationAwaitingAssume(
        convForTransfer,
        session?.chatwootUserId,
        osListForTransfer,
        { isPreAtendimento: transferIsPre }
      )
    )
      return;
    const currentTeam = teamIdFromConversation(convForTransfer);
    if (currentTeam != null && Number(currentTeam) === destId) return;

    setTransferringTeam(true);
    setError('');
    try {
      await transferConversationTeam(convId, destId);
      const stillMember = teams.some((t) => Number(t.id) === destId);
      if (!stillMember) {
        setConversations((prev) => prev.filter((c) => Number(c.id) !== convId));
        if (Number(selectedId) === convId) {
          setSelectedId(null);
          setSelectedConv(null);
          setMessages([]);
          setMessagesForConvId(null);
          setConversationOsList([]);
        }
      } else {
        patchConversationInList({
          id: convId,
          clearAssignee: true,
          team_id: destId,
          meta: {
            team: {
              id: destId,
              name: tenantTeams.find((t) => Number(t.id) === destId)?.name || null,
            },
          },
        });
        if (Number(teamId) !== destId) {
          setConversations((prev) => prev.filter((c) => Number(c.id) !== convId));
          if (Number(selectedId) === convId) {
            setSelectedId(null);
            setSelectedConv(null);
            setMessages([]);
            setMessagesForConvId(null);
            setConversationOsList([]);
          }
        } else if (Number(selectedId) === convId) {
          setSelectedConv((prev) =>
            prev
              ? {
                  ...prev,
                  team_id: destId,
                  assignee: undefined,
                  assigneeVerveUserId: null,
                  hasAssigneeProfilePhoto: false,
                  meta: {
                    ...(prev.meta || {}),
                    assignee: undefined,
                    team: {
                      id: destId,
                      name: tenantTeams.find((t) => Number(t.id) === destId)?.name || null,
                    },
                  },
                }
              : prev
          );
        }
      }
      await loadTeamBuckets(teams);
      await loadConversations({ silent: true });
      onBadgesRefresh?.();
    } catch (e) {
      setError(e.message || 'Falha ao transferir conversa');
    } finally {
      setTransferringTeam(false);
      setDragConvId(null);
      setDropTeamId(null);
    }
  };

  const handleOpenOsCreated = async ({ osNumber, equipment, defect }) => {
    if (!selectedId || !osNumber) return;
    try {
      await sendChatMessage(selectedId, {
        private: false,
        content: buildOsCardContent({
          osNumber,
          serie: equipment?.serie || equipment?.numSerie || '',
          patrimonio: equipment?.patrimonio || '',
          defect: defect?.nome || '',
        }),
      });
      await loadThread(selectedId, { silent: true, markRead: false });
      await loadConversations({ silent: true });
      onBadgesRefresh?.();
    } catch (e) {
      setError(e.message || 'OS gerada, mas falha ao enviar confirmação no chat');
    }
  };

  const node = (
    <div
      className={`bch-overlay ${layoutClass}${underHeader ? ' bch-overlay--under-header' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Chat"
    >
      {layout !== BOARD_CHAT_LAYOUTS.fullscreen ? (
        <div
          className="bch-overlay-backdrop"
          onClick={layout === BOARD_CHAT_LAYOUTS.modal ? onClose : undefined}
          aria-hidden="true"
        />
      ) : null}

      <div className={`bch-panel ${layoutClass}`}>
        <div className="bch-body">
          <div className="bch-rail-left">
            {openEmailFlow ? (
              <BoardChatEmailReminderPanel
                className={openEmailPanelVisible ? 'is-open' : 'is-closing'}
                conversationId={selectedId}
                protocolLabel={
                  isPreAtendimento
                    ? (effectiveOsList.length > 0
                        ? `OS #${effectiveOsList.map((item) => item.osId).join(', #')}`
                        : '')
                    : (selectedId != null ? `#protocolo ${selectedId}` : '')
                }
                contactEmail={
                  usuarioEmail && usuarioEmail !== '—' ? usuarioEmail : ''
                }
                fromOptions={emailMailOptions.fromOptions}
                primarySource={emailMailOptions.source}
                portalBaseUrl={emailMailOptions.portalBaseUrl || ''}
                onCancel={handleCloseEmailFlow}
                onSent={(count) => {
                  setEmailReminderCount(Number(count) || 0);
                  if (selectedId) {
                    loadThread(selectedId, { silent: true, markRead: false });
                  }
                }}
              />
            ) : openOsFlow ? (
              <BoardChatOpenOsPanel
                className={openOsPanelVisible ? 'is-open' : 'is-closing'}
                contracts={contratos}
                portalCustomer={portalCustomer}
                contactName={contactName}
                onCancel={handleCloseOsFlow}
                onCreated={handleOpenOsCreated}
              />
            ) : (
              <>
            <aside className="bch-card bch-card--nav">
              <div className="bch-card-head">
                <h2 className="bch-card-title">Caixa de entrada</h2>
              </div>
              <div className="bch-nav-scroll">
                <div className="bch-nav-customer-group">
                  <button type="button" className="bch-nav-item bch-nav-customer is-active">
                    <span className="bch-nav-item-icon">
                      <IconAffiliate size={16} stroke={1.8} />
                    </span>
                    <span className="bch-nav-item-label">Customer</span>
                    <CountBadge value={unreadMessagesTotal} />
                  </button>
                  <div className="bch-nav-customer-body">
                    <div className="bch-sec-head">
                      <span className="bch-sec-head-icon" aria-hidden>
                        <IconSitemap size={15} stroke={1.8} />
                      </span>
                      <span className="bch-sec-head-label">Seus setores</span>
                    </div>
                    {teams.length === 0 ? (
                      <p className="bch-empty" style={{ padding: '8px 4px', textAlign: 'left' }}>
                        Nenhum time vinculado
                      </p>
                    ) : (
                      <div className="bch-sec-tree" role="list" aria-label="Seus setores">
                        {teams.map((t) => {
                          const teamLabel = (t.description || '').trim() || t.name || '—';
                          const buckets = teamBuckets[Number(t.id)] || {
                            nova: 0,
                            pending: 0,
                            open: 0,
                            unread: 0,
                          };
                          const active = Number(teamId) === Number(t.id);
                          const dropActive = Number(dropTeamId) === Number(t.id);
                          const hasUnreadOrNew =
                            Number(buckets.unread) > 0 || Number(buckets.nova) > 0;
                          return (
                            <button
                              key={t.id}
                              type="button"
                              role="listitem"
                              className={[
                                'bch-nav-item',
                                'bch-nav-team-item',
                                active ? 'is-active' : '',
                                dropActive ? 'is-drop-target' : '',
                                hasUnreadOrNew ? 'has-unread' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() => {
                                setTeamId(t.id);
                                setSelectedId(null);
                              }}
                              onDragOver={(e) => {
                                if (!dragConvId) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                if (Number(dropTeamId) !== Number(t.id)) setDropTeamId(t.id);
                              }}
                              onDragLeave={() => {
                                if (Number(dropTeamId) === Number(t.id)) setDropTeamId(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                const raw = e.dataTransfer.getData('text/plain') || dragConvId;
                                const convId = Number(raw);
                                setDropTeamId(null);
                                setDragConvId(null);
                                if (!convId) return;
                                const dragged = conversations.find((c) => Number(c.id) === convId);
                                if (conversationStatusOf(dragged) === 'resolved') return;
                                handleTransferTeam(convId, t.id);
                              }}
                            >
                              <span className="bch-nav-team-item-body">
                                <span className="bch-nav-item-label">
                                  {hasUnreadOrNew ? (
                                    <span className="bch-team-unread-dot" aria-hidden="true" />
                                  ) : null}
                                  <span className="bch-nav-team-name">
                                    {teamLabel}
                                    <span className="bch-nav-team-total">
                                      {' '}
                                      ({buckets.nova + buckets.pending + buckets.open})
                                    </span>
                                  </span>
                                </span>
                                <span className="bch-team-counts" aria-label={`Contagens de ${teamLabel}`}>
                                  <span
                                    className="bch-team-count bch-team-count--nova"
                                    aria-label="Nova"
                                    {...tipProps('Nova')}
                                  >
                                    <IconBellPlus size={11} stroke={1.8} aria-hidden />
                                    <span>{buckets.nova}</span>
                                  </span>
                                  <span
                                    className="bch-team-count bch-team-count--pending"
                                    aria-label="Pendente"
                                    {...tipProps('Pendente')}
                                  >
                                    <IconCircleDashed size={11} stroke={1.8} aria-hidden />
                                    <span>{buckets.pending}</span>
                                  </span>
                                  <span
                                    className="bch-team-count bch-team-count--open"
                                    aria-label="Aberta"
                                    {...tipProps('Aberta')}
                                  >
                                    <IconMessage size={11} stroke={1.8} aria-hidden />
                                    <span>{buckets.open}</span>
                                  </span>
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </aside>

            <section className="bch-card bch-card--list">
              <div className="bch-card-head">
                <h2 className="bch-card-title">Menções</h2>
                <button
                  type="button"
                  className={`bch-icon-btn bch-list-refresh-btn${listRefreshing ? ' is-refreshing' : ''}`}
                  onClick={handleManualListRefresh}
                  disabled={listRefreshing}
                  aria-label="Atualizar conversas"
                  title="Atualizar conversas"
                >
                  <IconRefresh size={15} stroke={1.75} aria-hidden />
                </button>
              </div>
              <div className="bch-list-toolbar">
                <div className="bch-list-toolbar-row">
                  <div className="bch-dd">
                    <button
                      type="button"
                      className="bch-dd-trigger"
                      onClick={() =>
                        setOpenDropdown((v) => (v === 'status' ? null : 'status'))
                      }
                    >
                      <span className="bch-dd-label">{activeFilter.label}</span>
                      <CountBadge value={openCount} className="bch-count-badge--plain" />
                      <ChevronDown size={13} color="#9a9ba6" />
                    </button>
                    {openDropdown === 'status' ? (
                      <div className="bch-dd-menu" role="listbox" aria-label="Filtro de status">
                        {STATUS_FILTERS.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            role="option"
                            aria-selected={statusFilter === f.id}
                            className={`bch-dd-item${statusFilter === f.id ? ' is-active' : ''}`}
                            onClick={() => {
                              setStatusFilter(f.id);
                              if (f.id !== 'resolved') {
                                setResolvedFilters(EMPTY_RESOLVED_FILTERS);
                              }
                              setOpenDropdown(null);
                              setSelectedId(null);
                            }}
                          >
                            <span className="bch-dd-item-label">{f.label}</span>
                            {f.id !== 'resolved' ? (
                              <CountBadge
                                value={statusFilterCounts[f.id] ?? 0}
                                className="bch-count-badge--plain"
                              />
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <div className="bch-dd bch-dd--sort">
                    <button
                      type="button"
                      className="bch-dd-trigger"
                      onClick={() => setOpenDropdown((v) => (v === 'sort' ? null : 'sort'))}
                    >
                      <span className="bch-dd-muted">
                        {sortOrder === 'desc' ? 'Recentes' : 'Mais antigas'}
                      </span>
                      <ChevronDown size={13} color="#9a9ba6" />
                    </button>
                    {openDropdown === 'sort' ? (
                      <div className="bch-dd-menu bch-dd-menu--right" role="listbox" aria-label="Ordenação">
                        <button
                          type="button"
                          role="option"
                          aria-selected={sortOrder === 'desc'}
                          className={`bch-dd-item${sortOrder === 'desc' ? ' is-active' : ''}`}
                          onClick={() => {
                            setSortOrder('desc');
                            setOpenDropdown(null);
                          }}
                        >
                          <span className="bch-dd-item-label">Recentes</span>
                        </button>
                        <button
                          type="button"
                          role="option"
                          aria-selected={sortOrder === 'asc'}
                          className={`bch-dd-item${sortOrder === 'asc' ? ' is-active' : ''}`}
                          onClick={() => {
                            setSortOrder('asc');
                            setOpenDropdown(null);
                          }}
                        >
                          <span className="bch-dd-item-label">Mais antigas</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="bch-list-count-row">
                  <div className="bch-list-count">
                    {sortedConversations.length === 1
                      ? '1 conversa'
                      : `${sortedConversations.length} conversas`}
                  </div>
                  {statusFilter === 'resolved' ? (
                    <div className="bch-resolved-list-actions">
                      <BoardChatResolvedFilters
                        conversations={conversations}
                        filters={resolvedFilters}
                        onChange={setResolvedFilters}
                        teamId={teamId}
                        onOpenConversation={(id) => setSelectedId(id)}
                        tipProps={tipProps}
                      />
                      <button
                        type="button"
                        className="bch-resolved-export-btn"
                        disabled={resolvedFiltersCount === 0 || exportingResolved}
                        aria-label="Exportar conversas resolvidas filtradas"
                        {...(resolvedFiltersCount > 0 && !exportingResolved
                          ? tipProps('Exportar conversas filtradas')
                          : {})}
                        onClick={handleExportResolved}
                      >
                        <Download size={14} strokeWidth={1.9} aria-hidden />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="bch-list-scroll">
                {listLoading ? (
                  <div className="bch-loading">Carregando…</div>
                ) : useGroupedList ? (
                  (clientGroupedList ? visibleClientGroups : visibleListGroups).length === 0 ? (
                    <div className="bch-empty">Nenhuma conversa nesta caixa.</div>
                  ) : (
                    (clientGroupedList ? visibleClientGroups : visibleListGroups).map((group) => {
                      const items = clientGroupedList
                        ? group.items || []
                        : groupedConversations[group.id] || [];
                      const isOpen = listGroupsOpen[group.id] !== false;
                      const groupUnread = clientGroupedList
                        ? items.reduce((sum, c) => {
                            const n = Number(c?.unread_count ?? c?.unreadCount);
                            return sum + (Number.isFinite(n) && n > 0 ? n : 0);
                          }, 0)
                        : 0;
                      const groupUnreadLabel = clientGroupedList ? formatBadge(groupUnread) : null;
                      return (
                        <div key={group.id} className="bch-list-group">
                          <button
                            type="button"
                            className="bch-list-group-head"
                            aria-expanded={isOpen}
                            onClick={() =>
                              setListGroupsOpen((prev) => ({
                                ...prev,
                                [group.id]: !isOpen,
                              }))
                            }
                          >
                            <ChevronDown
                              size={13}
                              className={`bch-list-group-chevron${isOpen ? '' : ' is-collapsed'}`}
                            />
                            <span className="bch-list-group-label">{group.label}</span>
                            <span className="bch-list-group-meta">
                              {groupUnreadLabel ? (
                                <span
                                  className="bch-badge bch-badge--unread bch-list-group-unread"
                                  aria-label={`${groupUnreadLabel} conversas novas neste cliente`}
                                  title="Conversas novas (não lidas) neste cliente"
                                >
                                  {groupUnreadLabel}
                                </span>
                              ) : null}
                              <span className="bch-list-group-count">{items.length}</span>
                            </span>
                          </button>
                          {isOpen ? (
                            <div className="bch-list-group-body">
                              {items.map((c) => renderConversationItem(c))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )
                ) : sortedConversations.length === 0 ? (
                  <div className="bch-empty">Nenhuma conversa nesta caixa.</div>
                ) : (
                  sortedConversations.map((c) => renderConversationItem(c))
                )}
              </div>
            </section>
              </>
            )}
          </div>

          <div
            className={[
              'bch-rail-thread',
              threadLeaving ? 'is-leaving' : '',
              threadActionsBusy && !threadLeaving ? 'is-soft-busy' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <section className="bch-card bch-card--thread">
              {!selectedId ? (
                <div className="bch-thread-placeholder">
                  <div className="bch-thread-empty">
                    <IconMessage
                      size={48}
                      stroke={1.25}
                      className="bch-thread-empty-icon"
                      aria-hidden
                    />
                    <h3 className="bch-thread-empty-title">Selecione uma conversa</h3>
                    <p className="bch-thread-empty-desc">
                      Escolha uma conversa na lista à esquerda para visualizar as mensagens.
                    </p>
                    <div className="bch-thread-empty-stats" aria-label="Resumo da caixa">
                      <div className="bch-thread-empty-stat">
                        <span className="bch-thread-empty-stat-ico bch-thread-empty-stat-ico--nova">
                          <IconBellPlus size={16} stroke={1.8} aria-hidden />
                        </span>
                        <span className="bch-thread-empty-stat-label">Nova</span>
                        <span className="bch-thread-empty-stat-count">{inboxBuckets.nova}</span>
                      </div>
                      <div className="bch-thread-empty-stat">
                        <span className="bch-thread-empty-stat-ico bch-thread-empty-stat-ico--pending">
                          <IconCircleDashed size={16} stroke={1.8} aria-hidden />
                        </span>
                        <span className="bch-thread-empty-stat-label">Pendente</span>
                        <span className="bch-thread-empty-stat-count">{inboxBuckets.pending}</span>
                      </div>
                      <div className="bch-thread-empty-stat">
                        <span className="bch-thread-empty-stat-ico bch-thread-empty-stat-ico--open">
                          <IconMessage size={16} stroke={1.8} aria-hidden />
                        </span>
                        <span className="bch-thread-empty-stat-label">Aberta</span>
                        <span className="bch-thread-empty-stat-count">{inboxBuckets.open}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bch-card-head bch-card-head--thread">
                    <h3 className="bch-card-title">{contactName}</h3>
                    <div className="bch-thread-head-actions">
                      <div className="bch-status-pills">
                        {assignee ? (
                          <button
                            type="button"
                            className={`bch-pill bch-pill--icon${
                              returningToQueue ? ' is-busy' : ''
                            }`}
                            aria-label="Devolver para fila"
                            aria-busy={returningToQueue || undefined}
                            disabled={
                              threadActionsBusy ||
                              isResolvedConversation ||
                              actionsLocked
                            }
                            onClick={handleReturnToQueue}
                          >
                            <span className="bch-pill-face">
                              {returningToQueue ? (
                                <Loader2 size={14} className="bch-spin" aria-hidden />
                              ) : (
                                <IconUserMinus size={14} stroke={1.5} aria-hidden />
                              )}
                            </span>
                            <span className="bch-pill-label">Devolver para fila</span>
                          </button>
                        ) : null}
                        {canLeaveAttendance ? (
                          <button
                            type="button"
                            className={`bch-pill bch-pill--icon${
                              returningToQueue ? ' is-busy' : ''
                            }`}
                            aria-label="Sair do atendimento"
                            aria-busy={returningToQueue || undefined}
                            disabled={threadActionsBusy}
                            onClick={handleLeaveAttendance}
                            {...(peerOsBannerText
                              ? tipProps({
                                  title: 'Sair do atendimento',
                                  rows: [
                                    {
                                      label: 'Efeito',
                                      value:
                                        'Sai desta conversa sem Resolver. As OS de outros atendentes continuam.',
                                    },
                                    ...peerOsTipRows,
                                  ],
                                })
                              : {})}
                          >
                            <span className="bch-pill-face">
                              {returningToQueue ? (
                                <Loader2 size={14} className="bch-spin" aria-hidden />
                              ) : (
                                <IconLogout size={14} stroke={1.5} aria-hidden />
                              )}
                            </span>
                            <span className="bch-pill-label">Sair do atendimento</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className={`bch-pill bch-pill--icon${
                            statusBusy === 'pending' ? ' is-busy' : ''
                          }`}
                          aria-label="Pendente"
                          aria-busy={statusBusy === 'pending' || undefined}
                          disabled={
                            threadActionsBusy ||
                            isResolvedConversation ||
                            actionsLocked
                          }
                          onClick={() => handleStatus('pending')}
                        >
                          <span className="bch-pill-face">
                            {statusBusy === 'pending' ? (
                              <Loader2 size={14} className="bch-spin" aria-hidden />
                            ) : (
                              <IconCircleDashed size={14} stroke={2} />
                            )}
                          </span>
                          <span className="bch-pill-label">Pendente</span>
                        </button>
                        {canReopenConversation ? (
                          <button
                            type="button"
                            className={`bch-pill bch-pill--icon${
                              statusBusy === 'open' ? ' is-busy' : ''
                            }`}
                            aria-label="Reabrir"
                            aria-busy={statusBusy === 'open' || undefined}
                            disabled={threadActionsBusy || actionsLocked}
                            onClick={() => handleStatus('open')}
                          >
                            <span className="bch-pill-face">
                              {statusBusy === 'open' ? (
                                <Loader2 size={14} className="bch-spin" aria-hidden />
                              ) : (
                                <IconArrowBackUp size={14} stroke={2} />
                              )}
                            </span>
                            <span className="bch-pill-label">Reabrir</span>
                          </button>
                        ) : null}
                        <span
                          className="bch-pill-tip-wrap"
                          {...(resolveBlockedTip ? tipProps(resolveBlockedTip) : {})}
                        >
                          <button
                            type="button"
                            className={[
                              'bch-pill',
                              awaitingAssumeAttendance || primaryAction === 'resolved'
                                ? 'bch-pill--primary'
                                : '',
                              assumeAttendanceBusy || statusBusy === 'resolved'
                                ? 'is-busy'
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            aria-busy={
                              (awaitingAssumeAttendance
                                ? assumeAttendanceBusy
                                : statusBusy === 'resolved') || undefined
                            }
                            aria-disabled={resolveDisabled || undefined}
                            disabled={resolveDisabled}
                            onClick={() => {
                              if (awaitingAssumeAttendance) {
                                handleAssumeAttendance();
                                return;
                              }
                              if (resolveBlockedByPendingOs || interactionLocked) return;
                              handleStatus('resolved');
                            }}
                          >
                            {awaitingAssumeAttendance ? (
                              assumeAttendanceBusy ? (
                                <Loader2 size={13} className="bch-spin" aria-hidden />
                              ) : (
                                <IconUserShare size={13} stroke={2} aria-hidden />
                              )
                            ) : statusBusy === 'resolved' ? (
                              <Loader2 size={13} className="bch-spin" aria-hidden />
                            ) : primaryAction === 'resolved' ? (
                              <Check size={13} strokeWidth={2.4} />
                            ) : null}
                            {awaitingAssumeAttendance
                              ? 'Assumir atendimento'
                              : 'Resolver'}
                          </button>
                        </span>
                      </div>
                    </div>
                  </div>
                  {sessions.length > 0 ? (
                    <div className="bch-sessions" aria-label="Histórico de atendimentos">
                      <div className="bch-sessions-list bch-sessions-list--compact">
                        {pastSessionEntries.length > 0 ? (
                          <button
                            type="button"
                            className={`bch-sessions-past-toggle${showPastSessions ? ' is-open' : ''}`}
                            onClick={() => {
                              setShowPastSessions((v) => {
                                if (v) setPastSessionsVisible(8);
                                return !v;
                              });
                            }}
                          >
                            <IconHistory size={14} stroke={1.8} />
                            <span>
                              {showPastSessions
                                ? 'Ocultar conversas anteriores'
                                : 'Ver conversas anteriores'}
                            </span>
                            <span className="bch-sessions-past-count">
                              {pastSessionEntries.length}
                            </span>
                          </button>
                        ) : null}
                        {ongoingSessionEntries.map((entry) => renderSessionItem(entry))}
                        {showPastSessions ? (
                          <div
                            className="bch-sessions-past"
                            onScroll={(e) => {
                              const el = e.currentTarget;
                              if (
                                el.scrollTop + el.clientHeight >=
                                el.scrollHeight - 24
                              ) {
                                setPastSessionsVisible((n) =>
                                  Math.min(n + 8, pastSessionEntries.length)
                                );
                              }
                            }}
                          >
                            {visiblePastSessionEntries.map((entry) => renderSessionItem(entry))}
                            {visiblePastSessionEntries.length < pastSessionEntries.length ? (
                              <p className="bch-sessions-past-more">Carregando mais…</p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {peerOsBannerText ? (
                    <div className="bch-peer-os-banner" role="status">
                      <span className="bch-peer-os-banner-text">{peerOsBannerText}</span>
                      {canLeaveAttendance ? (
                        <button
                          type="button"
                          className="bch-peer-os-banner-leave"
                          disabled={threadActionsBusy}
                          onClick={handleLeaveAttendance}
                        >
                          Sair do atendimento
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {awaitingAssumeAttendance ? (
                    <div className="bch-view-only-banner" role="status">
                      Somente visualização · assuma o atendimento para responder
                    </div>
                  ) : interactionLocked ? (
                    <div className="bch-view-only-banner" role="status">
                      {osDoneReadOnly
                        ? 'Somente visualização · sua OS já foi encaminhada ou fechada'
                        : `Somente visualização${
                            assignee?.name ? ` · atendido por @${assignee.name}` : ''
                          }`}
                    </div>
                  ) : null}

                  {osDeletedNotice?.text ? (
                    <div className="bch-os-deleted-banner" role="status">
                      <span className="bch-os-deleted-banner-text">{osDeletedNotice.text}</span>
                      <button
                        type="button"
                        className="bch-os-deleted-banner-dismiss"
                        aria-label="Fechar aviso"
                        onClick={() => setOsDeletedNotice(null)}
                      >
                        <X size={12} strokeWidth={2.25} aria-hidden />
                      </button>
                    </div>
                  ) : null}

                  <div
                    key={`thread-msgs-${threadEnterKey}-${selectedId || 'none'}-${composerOsOptions.length > 1 ? composerOsId || 'all' : 'all'}`}
                    className={[
                      'bch-messages',
                      showThreadSkeleton ? 'is-switching' : '',
                      threadLoadVisible && isThreadMismatch ? 'is-loading-soft' : '',
                      !isThreadMismatch && threadEnterKey > 0 ? 'is-enter' : '',
                      threadLoading &&
                      messages.length > 0 &&
                      Number(messagesForConvId) === Number(selectedId)
                        ? 'is-refreshing'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {showThreadSkeleton ? (
                      <div className="bch-thread-skeleton" aria-busy="true" aria-live="polite">
                        <div className="bch-thread-skel-row bch-thread-skel-row--in">
                          <span className="bch-thread-skel-bubble" />
                        </div>
                        <div className="bch-thread-skel-row bch-thread-skel-row--out">
                          <span className="bch-thread-skel-bubble bch-thread-skel-bubble--short" />
                        </div>
                        <div className="bch-thread-skel-row bch-thread-skel-row--in">
                          <span className="bch-thread-skel-bubble bch-thread-skel-bubble--mid" />
                        </div>
                        {threadLoadVisible ? (
                          <div className="bch-thread-skel-hint">
                            <Loader2 size={14} className="bch-spin" aria-hidden />
                            <span>Carregando conversa…</span>
                          </div>
                        ) : null}
                      </div>
                    ) : threadMessages.length === 0 ? (
                      <div className="bch-empty">
                        {messages.length > 0 && composerOsId
                          ? `Sem mensagens para a OS ${composerOsId}.`
                          : 'Sem mensagens ainda.'}
                      </div>
                    ) : (
                      (() => {
                        const skip = new Set();
                        return threadMessages.map((m, i) => {
                          if (skip.has(i)) return null;
                          const kind = normalizeMessageType(m);
                          const optClass = m._optimistic ? ' is-optimistic' : '';
                          const citedOsId = messageCitedOsId(m);
                          if (kind === 'activity') {
                            return (
                              <div
                                key={m.id || `a-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className="bch-msg bch-msg--activity"
                              >
                                {translateActivityMessage(m.content)}
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            );
                          }

                          const equipmentChange = parseEquipmentChangeMessage(m);
                          if (equipmentChange) {
                            return (
                              <div
                                key={m.id || `eqc-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={`bch-msg bch-msg--eqc${optClass}`}
                              >
                                <BoardChatEquipmentChangeCard
                                  data={equipmentChange}
                                  createdAt={m.created_at}
                                />
                              </div>
                            );
                          }

                          const emailCard = parseEmailReminderCardContent(m.content);
                          if (emailCard) {
                            return (
                              <div
                                key={m.id || `er-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={`bch-msg bch-msg--private${optClass}`}
                              >
                                <BoardChatEmailReminderMessageCard
                                  data={emailCard}
                                  conversationId={selectedId}
                                />
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            );
                          }

                          const osCard = parseOsCardContent(m.content);
                          if (osCard) {
                            return (
                              <div
                                key={m.id || `os-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={`bch-msg bch-msg--outgoing${optClass}`}
                              >
                                <BoardChatOsMessageCard data={osCard} />
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            );
                          }

                          const preAtendCard = parsePreAtendimentoCardContent(m.content);
                          if (preAtendCard) {
                            const osNum = String(preAtendCard.osNumber || '').trim();
                            const osListReady =
                              !conversationOsLoading &&
                              Number(messagesForConvId) === Number(selectedId);
                            const matchedOsItem = (conversationOsList || []).find((item) => {
                              const id = String(item?.osId || '').trim();
                              if (!osNum || !id) return false;
                              return osIdsMatch(id, osNum);
                            });
                            const matchedActive =
                              matchedOsItem &&
                              String(matchedOsItem.status || '').toLowerCase() !== 'deleted'
                                ? matchedOsItem
                                : null;
                            const osDeleted = Boolean(osNum && osListReady && !matchedActive);
                            const directAttachments = attachmentsFromMessage(m);
                            const cardAttachments = directAttachments ? [...directAttachments] : [];
                            let nextIdx = i + 1;
                            while (nextIdx < threadMessages.length) {
                              const nextMsg = threadMessages[nextIdx];
                              if (normalizeMessageType(nextMsg) !== 'private') break;
                              if (parsePreAtendimentoCardContent(nextMsg.content)) break;
                              const nextAtts = attachmentsFromMessage(nextMsg);
                              if (!nextAtts || nextAtts.length === 0) break;
                              const cleanContent = String(nextMsg.content || '')
                                .replace(/__attachment__:\{[\s\S]*?\}/g, '')
                                .trim();
                              if (cleanContent.length > 0) break;
                              cardAttachments.push(...nextAtts);
                              skip.add(nextIdx);
                              nextIdx++;
                            }
                            return (
                              <div
                                key={m.id || `pa-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={`bch-msg bch-msg--pre-atend${optClass}`}
                              >
                                <BoardChatPreAtendimentoCard
                                  data={preAtendCard}
                                  createdAt={m.created_at}
                                  osData={matchedActive?.osData || null}
                                  osStatus={matchedActive?.status || 'pending'}
                                  osTrackable={matchedActive ? matchedActive.trackable !== false : false}
                                  osDeleted={osDeleted}
                                  onOpenOsMenu={
                                    osDeleted
                                      ? undefined
                                      : ({ osId, status, osData, trackable, rect }) => {
                                          hideTip();
                                          const osItem =
                                            matchedActive || { osId, status, osData };
                                          setOsChipMenu((prev) =>
                                            prev?.osId === String(osId)
                                              ? null
                                              : {
                                                  osId: String(osId),
                                                  status: String(status || 'pending').toLowerCase(),
                                                  osData: osData || null,
                                                  trackable: trackable !== false,
                                                  createdAt:
                                                    m.created_at || matchedActive?.createdAt || null,
                                                  unclaimed: isContestedUnassignedOs(
                                                    osItem,
                                                    conversationOsList
                                                  ),
                                                  consultOnly: isOsChipMenuConsultOnly(
                                                    osItem,
                                                    session?.chatwootUserId,
                                                    awaitingAssumeAttendance
                                                  ),
                                                  rect,
                                                }
                                          );
                                        }
                                  }
                                  tipProps={tipProps}
                                  attachments={cardAttachments}
                                />
                              </div>
                            );
                          }

                          const msgAttachments = attachmentsFromMessage(m);
                          const attach = msgAttachments[0] || null;
                          const allMsgImages =
                            msgAttachments.length > 0 &&
                            msgAttachments.every((a) =>
                              isImageContentType(a.contentType, a.filename)
                            );
                          const isImageAttach = allMsgImages;

                          if (isImageAttach && (kind === 'outgoing' || kind === 'incoming' || kind === 'private')) {
                            const group = [...msgAttachments];
                            const groupIndexes = [i];
                            const captions = attach.caption ? [attach.caption] : [];
                            let j = i + 1;
                            while (j < threadMessages.length) {
                              const mj = threadMessages[j];
                              if (normalizeMessageType(mj) !== kind) break;
                              const ajList = attachmentsFromMessage(mj);
                              if (
                                !ajList.length ||
                                !ajList.every((a) => isImageContentType(a.contentType, a.filename))
                              ) {
                                break;
                              }
                              // Não agrupa se houver legenda no meio (mantém ordem visual).
                              if (ajList.some((a) => a.caption)) break;
                              group.push(...ajList);
                              groupIndexes.push(j);
                              skip.add(j);
                              j += 1;
                            }
                            const lastMsg = threadMessages[groupIndexes[groupIndexes.length - 1]];
                            const captionText = captions[0] || null;
                            if (kind === 'incoming') {
                              return (
                                <div
                                  key={m.id || `img-${i}`}
                                  ref={(el) => {
                                    messageElsRef.current[i] = el;
                                  }}
                                  className="bch-msg bch-msg--incoming"
                                >
                                  <Avatar
                                    name={contactName}
                                    size={22}
                                    fontSize={10}
                                    photoUrl={contactPhotoByConv[selectedId]}
                                  />
                                  <div>
                                    <MessageAttachmentBubble attachments={group} />
                                    {captionText ? (
                                      <div className="bch-bubble bch-bubble--in bch-attach-caption-text">
                                        <MessageOsCitation
                                          osId={citedOsId}
                                          osList={effectiveOsList}
                                        />
                                        {renderChatBubbleText(captionText)}
                                      </div>
                                    ) : citedOsId ? (
                                      <MessageOsCitation
                                        osId={citedOsId}
                                        osList={effectiveOsList}
                                      />
                                    ) : null}
                                    <span className="bch-msg-meta">
                                      {messageTimeLabel(lastMsg.created_at)}
                                    </span>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={m.id || `img-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={
                                  (kind === 'private'
                                    ? 'bch-msg bch-msg--private'
                                    : 'bch-msg bch-msg--outgoing') + optClass
                                }
                              >
                                <MessageAttachmentBubble
                                  attachments={group}
                                  outgoing
                                />
                                {captionText ? (
                                  <div
                                    className={
                                      kind === 'private'
                                        ? 'bch-attach-caption-text'
                                        : 'bch-bubble bch-bubble--out bch-attach-caption-text'
                                    }
                                  >
                                    <MessageOsCitation
                                      osId={citedOsId}
                                      osList={effectiveOsList}
                                    />
                                    {renderChatBubbleText(captionText)}
                                  </div>
                                ) : (
                                  <MessageOsCitation
                                    osId={citedOsId}
                                    osList={effectiveOsList}
                                  />
                                )}
                                <span className="bch-msg-meta">
                                  {messageTimeLabel(lastMsg.created_at)}
                                </span>
                              </div>
                            );
                          }

                          if (msgAttachments.length > 0) {
                            const captionText = msgAttachments.find((a) => a.caption)?.caption || null;
                            const body = (
                              <>
                                <MessageAttachmentBubble
                                  attachments={msgAttachments}
                                  outgoing={kind === 'outgoing' || kind === 'private'}
                                />
                                {captionText ? (
                                  <div
                                    className={
                                      kind === 'private'
                                        ? 'bch-attach-caption-text'
                                        : kind === 'outgoing'
                                          ? 'bch-bubble bch-bubble--out bch-attach-caption-text'
                                          : 'bch-bubble bch-bubble--in bch-attach-caption-text'
                                    }
                                  >
                                    <MessageOsCitation
                                      osId={citedOsId}
                                      osList={effectiveOsList}
                                    />
                                    {renderChatBubbleText(captionText)}
                                  </div>
                                ) : (
                                  <MessageOsCitation
                                    osId={citedOsId}
                                    osList={effectiveOsList}
                                  />
                                )}
                              </>
                            );
                            if (kind === 'incoming') {
                              return (
                                <div
                                  key={m.id || `att-${i}`}
                                  ref={(el) => {
                                    messageElsRef.current[i] = el;
                                  }}
                                  className="bch-msg bch-msg--incoming"
                                >
                                  <Avatar
                                    name={contactName}
                                    size={22}
                                    fontSize={10}
                                    photoUrl={contactPhotoByConv[selectedId]}
                                  />
                                  <div>
                                    {body}
                                    <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={m.id || `att-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={
                                  (kind === 'private'
                                    ? 'bch-msg bch-msg--private'
                                    : 'bch-msg bch-msg--outgoing') + optClass
                                }
                              >
                                {body}
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            );
                          }

                          if (kind === 'private') {
                            return (
                              <div
                                key={m.id || `p-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={`bch-msg bch-msg--private${optClass}`}
                              >
                                <MessageOsCitation
                                  osId={citedOsId}
                                  osList={effectiveOsList}
                                />
                                {renderChatBubbleText(m.content)}
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            );
                          }
                          if (kind === 'outgoing') {
                            return (
                              <div
                                key={m.id || `o-${i}`}
                                ref={(el) => {
                                  messageElsRef.current[i] = el;
                                }}
                                className={`bch-msg bch-msg--outgoing${optClass}`}
                              >
                                <div className={bubbleClassFor('out', m.content)}>
                                  <MessageOsCitation
                                    osId={citedOsId}
                                    osList={effectiveOsList}
                                  />
                                  {renderChatBubbleText(m.content)}
                                </div>
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            );
                          }
                          return (
                            <div
                              key={m.id || `i-${i}`}
                              ref={(el) => {
                                messageElsRef.current[i] = el;
                              }}
                              className="bch-msg bch-msg--incoming"
                            >
                              <Avatar
                                name={contactName}
                                size={22}
                                fontSize={10}
                                photoUrl={contactPhotoByConv[selectedId]}
                              />
                              <div>
                                <div className={bubbleClassFor('in', m.content)}>
                                  <MessageOsCitation
                                    osId={citedOsId}
                                    osList={effectiveOsList}
                                  />
                                  {renderChatBubbleText(m.content)}
                                </div>
                                <span className="bch-msg-meta">{messageTimeLabel(m.created_at)}</span>
                              </div>
                            </div>
                          );
                        });
                      })()
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="bch-composer-wrap">
                    {error ? <p className="bch-error">{error}</p> : null}
                    {pendingOsHolder && !osDoneReadOnly ? (
                      <p className="bch-error bch-error--soft" role="status">
                        {holderWaitMessage}
                      </p>
                    ) : null}
                    {forwardedOsReplyNoticeOsId ? (
                      <div className="bch-forwarded-os-reply-notice" role="status">
                        <span className="bch-forwarded-os-reply-notice-text">
                          {`Esta ordem de serviço ${forwardedOsReplyNoticeOsId} está encaminhada`}
                        </span>
                        <button
                          type="button"
                          className="bch-forwarded-os-reply-notice-dismiss"
                          aria-label="Fechar aviso"
                          onClick={() => setForwardedOsReplyNoticeOsId(null)}
                        >
                          <X size={12} strokeWidth={2.25} aria-hidden />
                        </button>
                      </div>
                    ) : null}
                    {contactTyping ? (
                      <div className="bch-typing-indicator" aria-live="polite">
                        <span className="bch-typing-dots" aria-hidden="true">
                          <i />
                          <i />
                          <i />
                        </span>
                        <span>{contactTyping.name} está digitando…</span>
                      </div>
                    ) : null}
                    <div className={`bch-composer${composerLocked ? ' is-locked' : ''}`}>
                      <div className="bch-composer-top">
                      <div className="bch-dd" style={{ marginBottom: 0 }}>
                        <button
                          type="button"
                          className={`bch-composer-mode${asPrivate ? ' is-private' : ''}`}
                          disabled={composerLocked}
                          onClick={() =>
                            setOpenDropdown((v) => (v === 'reply' ? null : 'reply'))
                          }
                        >
                          <span className="bch-composer-mode-icon" aria-hidden="true">
                            {asPrivate ? (
                              <IconNote size={15} stroke={1.8} />
                            ) : (
                              <IconMessage size={15} stroke={1.8} />
                            )}
                          </span>
                          {asPrivate ? 'Anotação interna' : 'Resposta'}
                          <ChevronDown size={10} strokeWidth={2.6} />
                        </button>
                        {openDropdown === 'reply' ? (
                          <div className="bch-dd-menu bch-dd-menu--up" role="listbox" aria-label="Modo de resposta">
                            <button
                              type="button"
                              role="option"
                              aria-selected={!asPrivate}
                              className={`bch-dd-item${!asPrivate ? ' is-active' : ''}`}
                              disabled={Boolean(pendingOsHolder)}
                              onClick={() => {
                                if (pendingOsHolder) return;
                                if (composerOsIsSnoozed) {
                                  setSnoozedOsReplyPromptOsId(
                                    String(composerOsId || '').trim() || null
                                  );
                                  setAsPrivate(true);
                                  setOpenDropdown(null);
                                  return;
                                }
                                if (composerOsIsForwarded) {
                                  setForwardedOsReplyNoticeOsId(
                                    String(composerOsId || '').trim() || null
                                  );
                                  setAsPrivate(true);
                                  setOpenDropdown(null);
                                  return;
                                }
                                setForwardedOsReplyNoticeOsId(null);
                                setSnoozedOsReplyPromptOsId(null);
                                setAsPrivate(false);
                                setOpenDropdown(null);
                              }}
                            >
                              <span className="bch-composer-mode-icon" aria-hidden="true">
                                <IconMessage size={15} stroke={1.8} />
                              </span>
                              <span className="bch-dd-item-label">Resposta</span>
                            </button>
                            <button
                              type="button"
                              role="option"
                              aria-selected={asPrivate}
                              className={`bch-dd-item${asPrivate ? ' is-active' : ''}`}
                              onClick={() => {
                                setAsPrivate(true);
                                setSnoozedOsReplyPromptOsId(null);
                                setOpenDropdown(null);
                              }}
                            >
                              <span className="bch-composer-mode-icon" aria-hidden="true">
                                <IconNote size={15} stroke={1.8} />
                              </span>
                              <span className="bch-dd-item-label">Anotação interna</span>
                            </button>
                          </div>
                        ) : null}
                      </div>
                      {composerOsOptions.length > 0 && composerOsId ? (
                        <div className="bch-dd bch-composer-os-dd" style={{ marginBottom: 0 }}>
                          {composerOsStatusHint ? (
                            <span
                              className="bch-composer-os-status-hint"
                              aria-live="polite"
                            >
                              {`Esta OS foi ${composerOsStatusHint}`}
                            </span>
                          ) : null}
                          <button
                            type="button"
                            className={`bch-composer-os-mode${
                              composerOsOptions.length > 1 ? '' : ' is-single'
                            }`}
                            disabled={composerLocked || composerOsOptions.length < 2}
                            aria-haspopup={composerOsOptions.length > 1 ? 'listbox' : undefined}
                            aria-expanded={
                              composerOsOptions.length > 1
                                ? openDropdown === 'composer-os'
                                : undefined
                            }
                            aria-label={
                              composerOsOptions.length > 1
                                ? `Mensagem sobre OS ${composerOsId}. Escolher OS`
                                : `Mensagem sobre OS ${composerOsId}`
                            }
                            onClick={() => {
                              if (composerOsOptions.length < 2) return;
                              setOpenDropdown((v) =>
                                v === 'composer-os' ? null : 'composer-os'
                              );
                            }}
                            {...tipProps(
                              composerOsOptions.length > 1
                                ? 'Sobre qual OS é esta mensagem'
                                : `OS #${composerOsId}`,
                              'above'
                            )}
                          >
                            <span className="bch-composer-os-chip">
                              <span className="bch-pill-face" aria-hidden>
                                <Ticket size={12} strokeWidth={1.8} />
                              </span>
                              <span className="bch-os-chip-num">{composerOsId}</span>
                            </span>
                            {composerOsOptions.length > 1 ? (
                              <ChevronDown size={10} strokeWidth={2.6} />
                            ) : null}
                          </button>
                          {openDropdown === 'composer-os' && composerOsOptions.length > 1 ? (
                            <div
                              className="bch-dd-menu bch-dd-menu--up bch-composer-os-menu"
                              role="listbox"
                              aria-label="OS da mensagem"
                            >
                              {composerOsOptions.map((opt) => {
                                const eq = opt.osData?.equipamento || {};
                                const modelo = eq.modelo ? String(eq.modelo).trim() : '';
                                const serie = String(
                                  eq.num_serie || eq.serie || eq.numSerie || ''
                                ).trim();
                                const patrimonio = String(
                                  eq.patrimonio || eq.tag || ''
                                ).trim();
                                const active = opt.osId === composerOsId;
                                const metaBits = [
                                  serie ? `Série ${serie}` : null,
                                  patrimonio ? `Pat ${patrimonio}` : null,
                                ].filter(Boolean);
                                return (
                                  <button
                                    key={opt.osId}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    className={`bch-dd-item bch-composer-os-item${
                                      active ? ' is-active' : ''
                                    }`}
                                    onClick={() => {
                                      setComposerOsId(opt.osId);
                                      setForwardedOsReplyNoticeOsId(null);
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    <span className="bch-composer-os-item-ico" aria-hidden>
                                      <Ticket size={12} strokeWidth={1.8} />
                                    </span>
                                    <span className="bch-composer-os-item-body">
                                      <span className="bch-composer-os-item-line">
                                        <span className="bch-composer-os-item-num">
                                          {opt.osId}
                                        </span>
                                        {modelo ? (
                                          <>
                                            <span className="bch-composer-os-item-dot" aria-hidden>
                                              ·
                                            </span>
                                            <span className="bch-composer-os-item-model">
                                              {modelo}
                                            </span>
                                          </>
                                        ) : null}
                                      </span>
                                      {metaBits.length ? (
                                        <span className="bch-composer-os-item-meta">
                                          {metaBits.join(' · ')}
                                        </span>
                                      ) : null}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      </div>
                      <ComposerAttachmentDrafts
                        items={attachmentDrafts}
                        onRemove={removeAttachmentDraft}
                      />
                      <ComposerVoiceRecorder
                        active={voiceRecorder.recording || voiceRecorder.busy}
                        busy={voiceRecorder.busy}
                        elapsedSec={voiceRecorder.elapsedSec}
                        onCancel={() => voiceRecorder.cancel()}
                        onVoiceNote={handleVoiceNote}
                        onTranscribe={handleVoiceTranscribe}
                      />
                      <textarea
                        ref={draftRef}
                        value={draft}
                        disabled={composerLocked}
                        onChange={(e) => {
                          setDraft(e.target.value);
                          if (!asPrivate) notifyAgentTyping();
                        }}
                        placeholder={
                          awaitingAssumeAttendance
                            ? 'Assuma o atendimento para responder'
                            : interactionLocked
                            ? assignee?.name
                              ? `Em atendimento por @${assignee.name}`
                              : 'Somente visualização'
                            : isResolvedConversation
                            ? 'Conversa resolvida'
                            : asPrivate
                              ? 'Escreva uma nota interna…'
                              : 'Escreva uma resposta...'
                        }
                        onKeyDown={(e) => {
                          if (composerLocked || (pendingOsHolder && !asPrivate)) return;
                          if (
                            e.key === 'Enter' &&
                            !e.shiftKey &&
                            !e.nativeEvent?.isComposing
                          ) {
                            e.preventDefault();
                            if (showCannedSuggestions && cannedSuggestions[0]) {
                              applyCannedContent(cannedSuggestions[0].content);
                              return;
                            }
                            handleSend();
                          }
                        }}
                      />
                      {showCannedSuggestions ? (
                        <div className="bch-canned-slash-menu" role="listbox" aria-label="Atalhos de respostas predefinidas">
                          {cannedSuggestions.length ? (
                            cannedSuggestions.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                className="bch-canned-slash-item"
                                role="option"
                                aria-selected="false"
                                onClick={() => applyCannedContent(item.content)}
                              >
                                <span className="bch-canned-slash-shortcut">
                                  /{item.shortcut}
                                </span>
                                <span className="bch-canned-slash-preview">{item.content}</span>
                              </button>
                            ))
                          ) : (
                            <span className="bch-canned-slash-empty">
                              Nenhum atalho encontrado
                            </span>
                          )}
                        </div>
                      ) : null}
                      <div className="bch-composer-foot">
                        <div className="bch-composer-tools">
                          <BoardChatCannedResponsesMenu
                            onPick={applyCannedContent}
                            onResponsesChange={setCannedResponses}
                            onOpen={() => {
                              setEmojiOpen(false);
                              setAiWriteOpen(false);
                              setOpenDropdown(null);
                            }}
                            tipProps={tipProps}
                            hideTip={hideTip}
                          />
                          <BoardChatAiWriteMenu
                            open={aiWriteOpen}
                            onOpenChange={setAiWriteOpen}
                            tipProps={tipProps}
                            hideTip={hideTip}
                            draft={draft}
                            onApply={(text) => setDraft(text)}
                            onOpen={() => {
                              setEmojiOpen(false);
                              setOpenDropdown(null);
                            }}
                          />
                          <button
                            type="button"
                            className={`bch-composer-tool bch-composer-tool--emoji${emojiOpen ? ' is-active' : ''}`}
                            aria-label="Adicionar emoji"
                            aria-expanded={emojiOpen}
                            {...tipProps('Adicionar emoji', 'above')}
                            onClick={() => {
                              hideTip();
                              setOpenDropdown(null);
                              setAiWriteOpen(false);
                              setEmojiOpen((v) => !v);
                            }}
                          >
                            <Smile size={16} strokeWidth={1.8} />
                          </button>
                          <button
                            type="button"
                            className="bch-composer-tool"
                            aria-label="Adicionar foto ou vídeo"
                            {...tipProps('Adicionar foto ou vídeo', 'above')}
                            onClick={() => {
                              hideTip();
                              imageInputRef.current?.click();
                            }}
                          >
                            <ImageIcon size={16} strokeWidth={1.8} />
                          </button>
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept={ACCEPT_MEDIA}
                            multiple
                            hidden
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              e.target.value = '';
                              if (files.length) handlePickAttachments(files);
                            }}
                          />
                          <VoiceMicButton
                            recording={voiceRecorder.recording}
                            tipProps={tipProps}
                            hideTip={hideTip}
                            onClick={() => {
                              if (!selectedId) {
                                setError('Selecione uma conversa para gravar');
                                return;
                              }
                              voiceRecorder.toggle();
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className={`bch-send${
                            draft.trim() ||
                            attachmentDrafts.some((d) => d.status === 'done')
                              ? ' is-ready'
                              : ' is-hidden'
                          }`}
                          aria-label="Enviar mensagem"
                          disabled={
                            composerSendLocked ||
                            sending ||
                            (!draft.trim() &&
                              !attachmentDrafts.some((d) => d.status === 'done')) ||
                            attachmentDrafts.some(
                              (d) =>
                                d.status === 'uploading' ||
                                d.status === 'validating' ||
                                d.status === 'pending'
                            )
                          }
                          {...tipProps('Enviar mensagem', 'above')}
                          onClick={handleSend}
                        >
                          <IconSend2 size={18} stroke={1.8} />
                        </button>
                        {emojiOpen ? (
                          <ComposerEmojiPicker
                            onPick={insertEmoji}
                            onClose={() => setEmojiOpen(false)}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>

          <div className={`bch-rail-details${detailsCollapsed ? ' is-collapsed' : ''}`}>
            <aside className={`bch-card bch-card--details${detailsCollapsed ? ' is-collapsed' : ''}`}>
              <div className="bch-card-head">
                {!detailsCollapsed ? <div className="bch-details-tab">Detalhes</div> : null}
                <button
                  type="button"
                  className="bch-details-collapse-btn"
                  aria-label={detailsCollapsed ? 'Expandir detalhes' : 'Minimizar detalhes'}
                  aria-pressed={detailsCollapsed}
                  {...tipProps(detailsCollapsed ? 'Expandir detalhes' : 'Minimizar detalhes')}
                  onClick={() => {
                    hideTip();
                    setDetailsCollapsed((v) => !v);
                  }}
                >
                  <IconChevronLeft
                    size={16}
                    stroke={1.8}
                    style={detailsCollapsed ? { transform: 'rotate(180deg)' } : undefined}
                  />
                </button>
              </div>
              {!detailsCollapsed ? (
                !selectedId ? (
                <div className="bch-empty" style={{ padding: '24px 18px', textAlign: 'left' }}>
                  Nenhuma conversa selecionada.
                </div>
              ) : (
                <div className="bch-details-scroll">
                  <div className="bch-details-grid bch-details-grid--assignment">
                    <span className="bch-details-k">Atendente</span>
                    <span className="bch-details-v">
                      {assignee ? (
                        <>
                          <Avatar
                            name={assignee.name}
                            size={16}
                            fontSize={8}
                            photoUrl={assigneePhotoUrl}
                          />
                          {assignee.name}
                        </>
                      ) : (
                        'Não atribuído'
                      )}
                    </span>
                    <span className="bch-details-k">Setor</span>
                    <span className="bch-details-v bch-details-v--team">
                      <span className="bch-details-team-name">{teamLabel || '—'}</span>
                      {selectedId && !isPreAtendimento ? (
                        <BoardChatTeamTransferMenu
                          currentTeamId={teamIdFromConversation(selectedConv) || teamId}
                          teams={tenantTeams}
                          loading={tenantTeamsLoading}
                          transferring={transferringTeam}
                          disabled={isResolvedConversation || actionsLocked}
                          onTransfer={(nextTeamId) => handleTransferTeam(selectedId, nextTeamId)}
                          tipProps={tipProps}
                          hideTip={hideTip}
                        />
                      ) : null}
                    </span>
                  </div>

                  {chatContactId && !portalCustomer ? (
                    <div className="bch-details-block bch-details-block--note">
                      <BoardChatContactNote
                        contactId={chatContactId}
                        note={chatContactNote}
                        tipProps={tipProps}
                        hideTip={hideTip}
                        readOnly={actionsLocked}
                        onSaved={(saved) => applyContactNoteLocal(chatContactId, saved)}
                      />
                    </div>
                  ) : null}

                  <div className="bch-details-block">
                    <button
                      type="button"
                      className="bch-details-toggle"
                      onClick={() => setClientInfoOpen((v) => !v)}
                    >
                      <IconBuilding size={14} stroke={1.8} />
                      <span className="bch-details-toggle-title bch-details-toggle-title--lg">
                        Informações do cliente
                      </span>
                      <ChevronDown
                        size={13}
                        color="#9a9ba6"
                        style={{
                          transform: clientInfoOpen ? 'rotate(180deg)' : undefined,
                        }}
                      />
                    </button>
                    {clientInfoOpen ? (
                      <>
                        {portalCustomerLoading ? (
                          <p className="bch-details-hint">Carregando dados do cliente…</p>
                        ) : null}
                        {!portalCustomerLoading && portalCustomerError && !portalCustomer ? (
                          <p className="bch-details-hint">{portalCustomerError}</p>
                        ) : null}
                        {!portalCustomerLoading && portalCustomer ? (
                          <>
                            <div className="bch-details-grid">
                              <span className="bch-details-k">Contrato</span>
                              <span className="bch-details-v">
                                {contratos.length > 1 ? (
                                  <button
                                    type="button"
                                    ref={contractsAnchorRef}
                                    className="bch-contracts-trigger"
                                    aria-expanded={contractsTooltipOpen}
                                    onClick={() => setContractsTooltipOpen((v) => !v)}
                                  >
                                    {contratoLabel}
                                  </button>
                                ) : (
                                  contratoLabel
                                )}
                              </span>
                              <span className="bch-details-k">{clienteLabel}</span>
                              <span className="bch-details-v">
                                <span className="bch-details-client-name">{clienteValue}</span>
                              </span>
                              <span className="bch-details-k">Usuário</span>
                              <span className="bch-details-v bch-details-v--user">
                                <span>{usuarioEmail}</span>
                                <RatingStars value={portalCustomer.avgStars} />
                                {chatContactId ? (
                                  <BoardChatContactNote
                                    contactId={chatContactId}
                                    note={chatContactNote}
                                    tipProps={tipProps}
                                    hideTip={hideTip}
                                    readOnly={actionsLocked}
                                    onSaved={(saved) => applyContactNoteLocal(chatContactId, saved)}
                                  />
                                ) : null}
                              </span>
                              <span className="bch-details-k">Cargo</span>
                              <span className="bch-details-v">
                                {!editingCargo ? (
                                  <span className="bch-details-with-edit">
                                    <span>{cargoValue}</span>
                                    {!actionsLocked ? (
                                      <button
                                        type="button"
                                        className="bch-edit-btn"
                                        onClick={startCargoEdit}
                                        aria-label="Editar cargo"
                                        {...tipProps('Editar cargo')}
                                      >
                                        <Pencil size={14} strokeWidth={2} />
                                      </button>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span className="bch-inline-edit-stack">
                                    <span className="bch-inline-edit">
                                      <input
                                        type="text"
                                        className="bch-edit-input"
                                        value={cargoDraft}
                                        onChange={(e) => setCargoDraft(e.target.value.slice(0, 160))}
                                        disabled={cargoSaving}
                                        maxLength={160}
                                        placeholder="Cargo"
                                        aria-label="Cargo"
                                      />
                                      <button
                                        type="button"
                                        className="bch-edit-btn bch-edit-btn--ok"
                                        onClick={saveCargoEdit}
                                        disabled={cargoSaving}
                                        title="Salvar cargo"
                                        aria-label="Salvar cargo"
                                      >
                                        <Check size={14} strokeWidth={2} />
                                      </button>
                                      <button
                                        type="button"
                                        className="bch-edit-btn"
                                        onClick={cancelCargoEdit}
                                        disabled={cargoSaving}
                                        title="Cancelar"
                                        aria-label="Cancelar edição de cargo"
                                      >
                                        <X size={14} strokeWidth={2} />
                                      </button>
                                    </span>
                                    {cargoEditError ? (
                                      <span className="bch-details-edit-error">{cargoEditError}</span>
                                    ) : null}
                                  </span>
                                )}
                              </span>
                            </div>
                            <div style={{ marginTop: 10 }}>
                              <div className="bch-phones-inline">
                              <div className="bch-details-k bch-details-k--with-edit">
                                <span>Telefones</span>
                                {!editingPhones && !actionsLocked ? (
                                  <button
                                    type="button"
                                    className="bch-edit-btn"
                                    onClick={startPhonesEdit}
                                    aria-label="Editar telefones"
                                    {...tipProps('Editar telefones')}
                                  >
                                    <Pencil size={14} strokeWidth={2} />
                                  </button>
                                ) : editingPhones ? (
                                  <span className="bch-inline-edit-actions">
                                    <button
                                      type="button"
                                      className="bch-edit-btn bch-edit-btn--ok"
                                      onClick={savePhonesEdit}
                                      disabled={phonesSaving}
                                      title="Salvar telefones"
                                      aria-label="Salvar telefones"
                                    >
                                      <Check size={14} strokeWidth={2} />
                                    </button>
                                    <button
                                      type="button"
                                      className="bch-edit-btn"
                                      onClick={cancelPhonesEdit}
                                      disabled={phonesSaving}
                                      title="Cancelar"
                                      aria-label="Cancelar edição de telefones"
                                    >
                                      <X size={14} strokeWidth={2} />
                                    </button>
                                  </span>
                                ) : null}
                              </div>
                              {!editingPhones ? (
                                <div className="bch-phone-list">
                                  <div className="bch-phone-row">
                                    <span>{foneFixoDisplay}</span>
                                    <IconPhone size={13} stroke={1.8} />
                                  </div>
                                  <div className="bch-phone-row">
                                    <span>{foneWhatsDisplay}</span>
                                    <IconBrandWhatsapp size={13} stroke={1.8} color="#25b358" />
                                  </div>
                                </div>
                              ) : (
                                <div className="bch-phone-list bch-phone-list--edit">
                                  <div className="bch-phone-row">
                                    <input
                                      type="text"
                                      className="bch-edit-input"
                                      value={foneFixoDraft}
                                      onChange={(e) =>
                                        setFoneFixoDraft(formatPhoneMaskLimited(e.target.value, 10))
                                      }
                                      disabled={phonesSaving}
                                      placeholder="Fone fixo (10 dígitos)"
                                      inputMode="numeric"
                                      aria-label="Fone fixo"
                                    />
                                    <IconPhone size={13} stroke={1.8} />
                                  </div>
                                  <div className="bch-phone-row">
                                    <input
                                      type="text"
                                      className="bch-edit-input"
                                      value={foneWhatsDraft}
                                      onChange={(e) =>
                                        setFoneWhatsDraft(formatPhoneMaskLimited(e.target.value, 11))
                                      }
                                      disabled={phonesSaving}
                                      placeholder="WhatsApp (11 dígitos)"
                                      inputMode="numeric"
                                      aria-label="Fone WhatsApp"
                                    />
                                    <IconBrandWhatsapp size={13} stroke={1.8} color="#25b358" />
                                  </div>
                                </div>
                              )}
                              </div>
                              {phonesEditError ? (
                                <p className="bch-details-edit-error">{phonesEditError}</p>
                              ) : null}
                              {!editingPhones ? <BoardChatAccessSites conversationId={selectedId} /> : null}
                            </div>
                            <ContractsTooltip
                              open={contractsTooltipOpen}
                              anchorRef={contractsAnchorRef}
                              onClose={() => setContractsTooltipOpen(false)}
                              contratos={contratos}
                            />
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </div>

                  <div className="bch-details-block">
                    <button
                      type="button"
                      className="bch-details-toggle"
                      onClick={() => setOpenOrdersOpen((v) => !v)}
                    >
                      <Ticket size={14} strokeWidth={1.8} />
                      <span className="bch-details-toggle-title bch-details-toggle-title--lg">
                        OS abertas
                      </span>
                      {effectiveOsList.length > 0 ? (
                        <span className="bch-details-block-count">
                          {effectiveOsList.length}
                        </span>
                      ) : null}
                      <ChevronDown
                        size={13}
                        color="#9a9ba6"
                        style={{
                          transform: openOrdersOpen ? 'rotate(180deg)' : undefined,
                        }}
                      />
                    </button>
                    {openOrdersOpen ? (
                      <div className="bch-details-os-section">
                        {conversationOsLoading && effectiveOsList.length === 0 ? (
                          <p className="bch-details-hint">Carregando ordens de serviço…</p>
                        ) : effectiveOsList.length > 0 ? (
                          <div className="bch-details-os-chips">
                            {effectiveOsList.map((item) => {
                              const osId = String(item.osId || '').trim();
                              const osStatus = String(item.status || 'pending').toLowerCase();
                              const osTrackable = item.trackable !== false;
                              const eqModelo = item.osData?.equipamento?.modelo;
                              const defDesc = item.osData?.defeito?.descricao;
                              const statusLabel =
                                osStatus === 'forwarded'
                                  ? 'Encaminhada'
                                  : osStatus === 'closed'
                                    ? 'Fechada'
                                    : osStatus === 'snoozed'
                                      ? 'Adiada'
                                      : 'Pendente';
                              const tipContent = {
                                title: `OS ${osId}`,
                                rows: [
                                  { label: 'Status', value: statusLabel },
                                  !osTrackable
                                    ? { label: 'Controle', value: 'Sem vínculo rastreável' }
                                    : null,
                                  eqModelo ? { label: 'Equipamento', value: String(eqModelo) } : null,
                                  defDesc ? { label: 'Defeito', value: String(defDesc) } : null,
                                ].filter(Boolean),
                              };

                              return (
                                <button
                                  key={osId}
                                  type="button"
                                  className={`bch-pill bch-os-chip${
                                    isContestedUnassignedOs(item, conversationOsList)
                                      ? ' bch-os-chip--unclaimed'
                                      : osStatus !== 'pending'
                                        ? ` bch-os-chip--${osStatus}`
                                        : ''
                                  }${!osTrackable ? ' bch-os-chip--untracked' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    hideTip();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setOsChipMenu((prev) =>
                                      prev?.osId === osId
                                        ? null
                                        : {
                                            osId,
                                            status: osStatus,
                                            osData: item.osData || null,
                                            trackable: osTrackable,
                                            createdAt: item.createdAt || null,
                                            unclaimed: isContestedUnassignedOs(
                                              item,
                                              conversationOsList
                                            ),
                                            consultOnly: isOsChipMenuConsultOnly(
                                              item,
                                              session?.chatwootUserId,
                                              awaitingAssumeAttendance
                                            ),
                                            rect,
                                          }
                                    );
                                  }}
                                  aria-label={`OS ${osId} · ${
                                    isContestedUnassignedOs(item, conversationOsList)
                                      ? 'Nova · sem atendente'
                                      : statusLabel
                                  }`}
                                  aria-haspopup="menu"
                                  {...tipProps(tipContent)}
                                >
                                  <span className="bch-pill-face">
                                    <Ticket size={13} strokeWidth={1.8} />
                                  </span>
                                  <span className="bch-os-chip-num">{osId}</span>
                                  {osStatus === 'forwarded' ? (
                                    <IconArrowForward
                                      size={12}
                                      stroke={2}
                                      className="bch-os-chip-status-ico"
                                      aria-hidden
                                    />
                                  ) : osStatus === 'closed' ? (
                                    <IconCircleCheck
                                      size={12}
                                      stroke={2}
                                      className="bch-os-chip-status-ico"
                                      aria-hidden
                                    />
                                  ) : osStatus === 'snoozed' ? (
                                    <IconClockPause
                                      size={12}
                                      stroke={2}
                                      className="bch-os-chip-status-ico"
                                      aria-hidden
                                    />
                                  ) : null}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="bch-details-hint">Nenhuma OS vinculada a este atendimento.</p>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="bch-details-block">
                    <button
                      type="button"
                      className="bch-details-toggle"
                      onClick={() => setMoreInfoOpen((v) => !v)}
                    >
                      <span className="bch-details-toggle-title bch-details-toggle-title--lg">
                        Ações
                      </span>
                      <ChevronDown
                        size={13}
                        color="#9a9ba6"
                        style={{
                          transform: moreInfoOpen ? 'rotate(180deg)' : undefined,
                        }}
                      />
                    </button>
                    {moreInfoOpen ? (
                      <div className="bch-details-actions-list">
                        <button
                          type="button"
                          className="bch-details-action-btn"
                          aria-label="Abrir ordem de serviço"
                          disabled={isResolvedConversation || actionsLocked}
                          onClick={handleOpenOsFlow}
                          {...(isResolvedConversation || actionsLocked
                            ? {}
                            : tipProps('Abrir ordem de serviço'))}
                        >
                          <span className="bch-details-action-icon" aria-hidden="true">
                            <Ticket size={15} strokeWidth={1.8} />
                          </span>
                          <span className="bch-details-action-label">Abrir ordem de serviço</span>
                        </button>
                        <span
                          className={
                            selectedId && !emailMailOptions.available
                              ? 'bch-details-action-tip-host'
                              : undefined
                          }
                          {...(selectedId && !emailMailOptions.available
                            ? tipProps('Não há email configurado')
                            : {})}
                        >
                          <button
                            type="button"
                            className="bch-details-action-btn"
                            aria-label="Lembrete por email"
                            disabled={
                              !selectedId ||
                              !emailMailOptions.available ||
                              actionsLocked
                            }
                            onClick={handleOpenEmailFlow}
                            {...(selectedId &&
                            emailMailOptions.available &&
                            !actionsLocked
                              ? tipProps('Lembrete por email')
                              : {})}
                          >
                            <span className="bch-details-action-icon" aria-hidden="true">
                              <IconMail size={15} stroke={1.8} />
                            </span>
                            <span className="bch-details-action-label">
                              Lembrete por email
                              {emailReminderCount > 0 ? (
                                <span className="bch-details-action-count">
                                  {emailReminderCount > 99 ? '99+' : emailReminderCount}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </span>
                        {validOsNumber ? (
                          <button
                            type="button"
                            className="bch-details-action-btn bch-details-action-btn--os-consult"
                            aria-label={`Consultar situação da OS ${validOsNumber}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleConsultOsSituacao}
                            {...tipProps(`Consultar situação da OS ${validOsNumber}`)}
                          >
                            <span className="bch-details-action-icon" aria-hidden="true">
                              <IconReportSearch size={15} stroke={1.8} />
                            </span>
                            <span className="bch-details-action-label">
                              {`Consultar situação da OS ${validOsNumber}`}
                            </span>
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              )
              ) : null}
            </aside>
          </div>

          {!layoutLocked ? (
            <div className="bch-rail-tools">
              <button
                type="button"
                className="bch-rail-tool"
                onClick={onClose}
                aria-label="Fechar"
                {...tipProps('Fechar', 'below')}
              >
                <X size={16} strokeWidth={1.9} />
              </button>
              <BoardChatLayoutSwitcher layout={layout} onChange={onLayoutChange} tipProps={tipProps} />
            </div>
          ) : null}
        </div>
      </div>
      <BchTipLayer tip={tip} />
      <BoardChatReturnToQueueMenu
        open={Boolean(returnQueuePicker)}
        anchorRect={returnQueuePicker?.rect}
        agents={handoffAgents}
        osIds={returnQueuePicker?.osIds || []}
        busy={returningToQueue}
        onClose={() => setReturnQueuePicker(null)}
        onPick={(toAgentId) => {
          finishReturnToQueue({
            handoffToAgentId: toAgentId,
            osIdsToHandoff: returnQueuePicker?.osIds || [],
          });
        }}
      />
      <BoardChatOsChipMenu
        open={Boolean(osChipMenu)}
        anchorRect={osChipMenu?.rect}
        osId={osChipMenu?.osId}
        status={osChipMenu?.status}
        trackable={osChipMenu?.trackable !== false}
        unclaimed={Boolean(osChipMenu?.unclaimed)}
        consultOnly={Boolean(osChipMenu?.consultOnly)}
        claimBusy={claimUnassignedBusy}
        agentCodtech={session?.agentCodtech || ''}
        handoffAgents={handoffAgents}
        handoffBusy={handoffBusy}
        tipProps={tipProps}
        onClose={() => setOsChipMenu(null)}
        onAssumir={() => {
          const id = String(osChipMenu?.osId || '').trim();
          if (!id || claimUnassignedBusy) return;
          handleClaimUnassignedOs(id);
        }}
        onConsultar={() => {
          const id = String(osChipMenu?.osId || '').trim();
          setOsChipMenu(null);
          hideTip();
          if (!id) return;
          if (typeof onOpenOrderDetails !== 'function') {
            setError(`Não foi possível abrir o detalhe da OS ${id}.`);
            return;
          }
          // Adia para o click atual não fechar o overlay recém-montado.
          window.setTimeout(() => {
            onOpenOrderDetails(id, { isPreAtendimento: true, source: 'open' });
          }, 50);
        }}
        onEncaminhar={() => {
          const id = osChipMenu?.osId;
          const convId = selectedId || selectedConv?.id;
          const st = String(osChipMenu?.status || 'pending').toLowerCase();
          const canAct =
            !actionsLocked &&
            osChipMenu?.trackable !== false &&
            (st === 'pending' || st === 'snoozed');
          const item = osChipMenu;
          setOsChipMenu(null);
          hideTip();
          if (!canAct || !id || !convId || osActionBusy) return;
          setForwardNoteItem({
            osId: id,
            conversationId: convId,
            osData: item?.osData || null,
          });
        }}
        onRepassar={async (toAgentId) => {
          const id = String(osChipMenu?.osId || '').trim();
          const convId = selectedId || selectedConv?.id;
          const st = String(osChipMenu?.status || 'pending').toLowerCase();
          const canAct =
            !actionsLocked &&
            osChipMenu?.trackable !== false &&
            (st === 'pending' || st === 'snoozed');
          if (!canAct || !id || !convId || handoffBusy) return;
          setHandoffBusy(true);
          setError('');
          try {
            await handoffConversationOs(convId, id, toAgentId);
            setOsChipMenu(null);
            hideTip();
            await reloadConversationOs();
            notifyConversationOsUpdated(convId, id, 'pending');
            loadConversations({ silent: true });
            onBadgesRefresh?.();
            try {
              await markConversationUnread(convId);
              const prevUnread = Number(
                (conversations.find((x) => Number(x.id) === Number(convId)) || selectedConv)
                  ?.unread_count
              );
              patchConversationInList({
                id: Number(convId),
                unread_count: Math.max(1, Number.isFinite(prevUnread) ? prevUnread : 1),
              });
              onBadgesRefresh?.();
            } catch {
              /* unread é best-effort (compartilhado no Chatwoot) */
            }
          } catch (e) {
            setError(e.message || 'Falha ao repassar OS');
          } finally {
            setHandoffBusy(false);
          }
        }}
        onAdiar={async () => {
          const id = String(osChipMenu?.osId || '').trim();
          const convId = selectedId || selectedConv?.id;
          const st = String(osChipMenu?.status || 'pending').toLowerCase();
          const canAct =
            !actionsLocked &&
            osChipMenu?.trackable !== false &&
            st === 'pending';
          if (!canAct || !id || !convId || handoffBusy) return;
          setHandoffBusy(true);
          setError('');
          try {
            await patchConversationOs(convId, id, { status: 'snoozed' });
            setOsChipMenu(null);
            hideTip();
            await reloadConversationOs();
            notifyConversationOsUpdated(convId, id, 'snoozed');
            onBadgesRefresh?.();
          } catch (e) {
            setError(e.message || 'Falha ao adiar OS');
          } finally {
            setHandoffBusy(false);
          }
        }}
        onRetomar={async () => {
          const id = String(osChipMenu?.osId || '').trim();
          const convId = selectedId || selectedConv?.id;
          const st = String(osChipMenu?.status || 'pending').toLowerCase();
          // Retomar a própria OS adiada (ex.: via repasse) não depende de assignee/somente leitura.
          const canAct = osChipMenu?.trackable !== false && st === 'snoozed';
          if (!canAct || !id || !convId || handoffBusy) return;
          setHandoffBusy(true);
          setError('');
          try {
            await patchConversationOs(convId, id, { status: 'pending' });
            setOsChipMenu(null);
            hideTip();
            await reloadConversationOs();
            notifyConversationOsUpdated(convId, id, 'pending');
            onBadgesRefresh?.();
          } catch (e) {
            setError(e.message || 'Falha ao retomar OS');
          } finally {
            setHandoffBusy(false);
          }
        }}
        onFechar={() => {
          const item = osChipMenu;
          const st = String(item?.status || 'pending').toLowerCase();
          const canAct =
            !actionsLocked &&
            item?.trackable !== false &&
            (st === 'pending' || st === 'snoozed');
          const hasTech = Boolean(String(session?.agentCodtech || '').trim());
          setOsChipMenu(null);
          hideTip();
          if (!canAct || !hasTech || !item?.osId) return;
          setCloseOsItem(item);
        }}
      />
      <BoardChatPreAtendimentoForwardNoteModal
        open={Boolean(forwardNoteItem)}
        osId={forwardNoteItem?.osId}
        onClose={() => setForwardNoteItem(null)}
        onContinue={() => {
          const item = forwardNoteItem;
          setForwardNoteItem(null);
          if (!item?.osId || typeof onEncaminharOs !== 'function') return;
          onEncaminharOs(item.osId, {
            conversationId: item.conversationId,
          });
        }}
      />
      <BoardChatPreAtendimentoCloseModal
        open={Boolean(closeOsItem)}
        osId={closeOsItem?.osId}
        osData={closeOsItem?.osData}
        createdAt={closeOsItem?.createdAt || null}
        conversationId={selectedId || selectedConv?.id}
        agentCodtech={session?.agentCodtech || ''}
        onClose={() => setCloseOsItem(null)}
        onClosed={async () => {
          const cid = selectedId || selectedConv?.id;
          const oid = closeOsItem?.osId;
          setCloseOsItem(null);
          await reloadConversationOs();
          notifyConversationOsUpdated(cid, oid, 'closed');
          onBadgesRefresh?.();
        }}
      />
      {snoozedOsReplyPromptOsId
        ? createPortal(
            <div
              className="bch-snoozed-os-confirm-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bch-snoozed-os-confirm-title"
            >
              <button
                type="button"
                className="bch-snoozed-os-confirm-backdrop"
                aria-label="Fechar"
                disabled={snoozedOsResumeBusy}
                onClick={() => setSnoozedOsReplyPromptOsId(null)}
              />
              <div className="bch-snoozed-os-confirm-modal">
                <h3 id="bch-snoozed-os-confirm-title" className="bch-snoozed-os-confirm-title">
                  Retomar OS
                </h3>
                <p className="bch-snoozed-os-confirm-message">
                  Para enviar como <strong>Resposta</strong>, retome antes a OS{' '}
                  <strong>{snoozedOsReplyPromptOsId}</strong>. Deseja retomar agora?
                </p>
                <div className="bch-snoozed-os-confirm-actions">
                  <button
                    type="button"
                    className="user-form-link-btn user-form-footer-btn"
                    disabled={snoozedOsResumeBusy}
                    onClick={() => setSnoozedOsReplyPromptOsId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn"
                    disabled={snoozedOsResumeBusy}
                    onClick={() => {
                      handleResumeSnoozedComposerOs();
                    }}
                  >
                    {snoozedOsResumeBusy ? 'Retomando…' : 'Retomar'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );

  return createPortal(node, document.body);
}
